"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const anime_es_js_1 = __importDefault(require("animejs/lib/anime.es.js"));
const tippy_js_1 = __importDefault(require("tippy.js"));
const exceljs_1 = __importDefault(require("exceljs"));
const Swal_1 = __importDefault(require("@/react/components/Swal"));
class RetentionSchedule {
    DEBUG_MODE = false;
    body_offset_top = 0;
    body_height = 0;
    is_scrolling_down = false;
    is_scrolling_right = false;
    scroll_direction;
    global_rows = [];
    starting_row;
    ending_row;
    rows = [];
    rows_h_translate = 0;
    starting_column_block;
    ending_column_block;
    column_blocks = [];
    columns = [];
    pinned_column_container = {
        renderContainer: document.createElement('div'),
        wrapper: document.createElement('div'),
        header: document.createElement('div'),
        offsetTop: 0,
        pinnedColumns: [],
        getPinOffset(index) {
            let o = 0;
            for (let i = 0; i < this.pinnedColumns.length; i++) {
                if (this.pinnedColumns[i].originalColumn.index > index) {
                    break;
                }
                o += this.pinnedColumns[i].originalColumn.width;
            }
            return o;
        },
        fixStartingPinOffsets(index, amount, direction) {
            let ret = [];
            for (let i = 0; i < this.pinnedColumns.length; i++) {
                const pinnedObj = this.pinnedColumns[i];
                if (pinnedObj.originalColumn.index > index) {
                    pinnedObj.setStartingPinOffset(pinnedObj.startingPinOffset + amount * (direction === '-' ? -1 : 1));
                    if (pinnedObj.isBeingPinned)
                        ret.push(pinnedObj.element, pinnedObj.originalColumn.element);
                }
            }
            return ret;
        }
    };
    headers = [];
    flattened_headers = [];
    drawer_buttons = [];
    is_column_transitioning = false;
    is_row_dragging = false;
    is_row_deleting = false;
    is_row_adding = false;
    is_row_resizing = false;
    cells_selected = [];
    highlighted_cells = new Set();
    currently_opened_header_dropdown;
    currently_hovered_row;
    cell_edit_dropdown = document.createElement('div');
    cell_edit_button = document.createElement('span');
    add_row_green_area = document.createElement('div');
    symbol_map = {};
    can_add_rows;
    can_delete_rows;
    can_edit_cells;
    can_move_rows;
    body_skeleton = document.createElement('div');
    loading_div = document.createElement('div');
    loading_text = document.createElement('div');
    loading_icon = document.createElement('div');
    events = {};
    scroll_container;
    wrapper = document.createElement('div');
    no_data_wrapper = document.createElement('div');
    row_processor = document.createElement('div');
    head = document.createElement('div');
    body = document.createElement('div');
    row_number_container = document.createElement('div');
    render_container = document.createElement('div');
    static CLASS_PREFIX = 'retention-schedule';
    static COLUMN_DEFAULT_WIDTH = 200;
    static COLUMN_PADDING_LEFT = '16px';
    static COLUMN_PADDING_RIGHT = '22px';
    static INTRINSIC_ROW_HEIGHT = 200;
    static COLUMN_ANIMATION_DURATION = 400;
    static DRAWER_BUTTON_WIDTH = 32;
    static HEADER_ENTRY_ANIMATION_DELAY = 0.05;
    static CELL_EDIT_CONTAINER_HEIGHT = 32;
    static DEFAULT_LOADING_TEXT = 'Loading your awesome schedule!';
    static DEFAULT_ERROR_TEXT = 'An error occured! Please try reloading the browser';
    static LOADING_ICON = 'hourglass_empty';
    static ERROR_ICON = 'emergency_home';
    static GET_MINIMUM_TEXT_WIDTH = (text, minWidth = 100) => {
        const div = document.createElement('div');
        div.style.padding = '16px';
        div.style.fontSize = '14px';
        div.style.position = 'absolute';
        div.style.visibility = 'invisible';
        div.style.whiteSpace = 'nowrap';
        div.style.fontWeight = 'bold';
        div.textContent = text;
        document.body.appendChild(div);
        const width = div.offsetWidth + 4 > minWidth ? div.offsetWidth + 4 : minWidth;
        div.remove();
        return width;
    };
    addEventListener(event, callback) {
        this.events[event] = callback;
    }
    setLoadingText(text) {
        this.loading_icon.textContent = RetentionSchedule.LOADING_ICON;
        this.loading_div.classList.remove('error');
        if (text)
            this.loading_text.textContent = text;
        else
            this.loading_text.textContent = RetentionSchedule.DEFAULT_LOADING_TEXT;
    }
    setErrorText(text) {
        this.loading_icon.textContent = RetentionSchedule.ERROR_ICON;
        this.loading_div.classList.add('error');
        if (text)
            this.loading_text.textContent = text;
        else
            this.loading_text.textContent = RetentionSchedule.DEFAULT_ERROR_TEXT;
    }
    getHeaders() {
        return this.headers;
    }
    getFlattenedHeaders() {
        return this.flattened_headers;
    }
    getHeader(title) {
        if (typeof title === 'number')
            return this.headers[title];
        else if (typeof title === 'string')
            return this.flattened_headers.find((header) => {
                if (header.title === title)
                    return true;
            });
    }
    getColumns() {
        return this.columns;
    }
    getColumn(col) {
        if (typeof col === 'number')
            return this.columns[col];
        else if (typeof col === 'string')
            return this.columns.find((column) => column.title === col);
    }
    getRows() {
        return this.rows;
    }
    getGlobalRows() {
        return this.global_rows;
    }
    getRow(rowId) {
        if (typeof rowId === 'number')
            return this.rows[rowId];
        else if (typeof rowId === 'string')
            return this.rows.find((row) => row.rowId.toString().toLowerCase() === rowId.toString().toLowerCase());
    }
    getGlobalRow(rowId) {
        if (typeof rowId === 'number')
            return this.global_rows[rowId];
        else if (typeof rowId === 'string')
            return this.global_rows.find((row) => row.rowId.toString().toLowerCase() === rowId.toString().toLowerCase());
    }
    searchRows(terms, options) {
        const { approximate = false, highlight = false } = options ?? {};
        const prevRows = this.rows;
        this.rows = [];
        this.highlighted_cells.forEach(x => x.deHighlight());
        if (terms.length === 0) {
            for (let i = 0; i < this.global_rows.length; i++) {
                this.rows.push(this.global_rows[i]);
            }
        }
        else {
            const newRowMap = new Map();
            for (let i = 0; i < this.global_rows.length; i++) {
                const row = this.global_rows[i];
                row.setLocalIndex(-1);
                for (let j = 0; j < terms.length; j++) {
                    const { searchTerms, columnIndexToSearch } = terms[j];
                    const col = this.columns[columnIndexToSearch];
                    const cell = row.cells[columnIndexToSearch];
                    const dataToCompareTo = Array.isArray(col.isEditable) ? col.dropdownItems?.get(cell.data)?.toString().toLowerCase() : cell.data.toString().toLowerCase();
                    const compFunction = (term) => {
                        term = term.toString().toLowerCase();
                        return approximate ? dataToCompareTo?.includes(term) : dataToCompareTo === term;
                    };
                    for (let k = 0; k < searchTerms.length; k++) {
                        const searchTerm = searchTerms[k].toLowerCase();
                        if (newRowMap.get(row.rowId))
                            break;
                        if (compFunction(searchTerm)) {
                            newRowMap.set(row.rowId, row);
                            if (highlight) {
                                cell.highlight(searchTerm);
                            }
                            break;
                        }
                    }
                    if (newRowMap.get(row.rowId))
                        break;
                }
            }
            newRowMap.forEach((row) => {
                this.rows.push(row);
            });
        }
        let err = false;
        if (this.rows.length === 0) {
            this.rows = prevRows;
            err = true;
        }
        for (let i = 0; i < this.rows.length; i++) {
            const row = this.rows[i];
            row.setIsRendered(false);
            row.setLocalIndex(i);
            row.setTranslate({
                vTranslate: RetentionSchedule.INTRINSIC_ROW_HEIGHT * i,
                hTranslate: this.rows_h_translate,
            });
        }
        this.#setBodyHeightAndAdjustPinnedContainer(this.rows.length > 0 ? RetentionSchedule.INTRINSIC_ROW_HEIGHT * this.rows.length : RetentionSchedule.INTRINSIC_ROW_HEIGHT * 2);
        this.#renderCurrentViewport();
        this.body.classList.remove('animate-in');
        this.body.classList.add('animate-in');
        if (err) {
            throw new Error('Search for the term returned no results!');
        }
        return this.rows;
    }
    searchRowsByColumn(term, column) {
        if (term === '')
            return this.rows;
        term = term.toLowerCase();
        if (typeof column === 'number') {
            const col = this.columns[column];
            return this.searchRowsByColumn(term, col);
        }
        return this.searchRows([{ searchTerms: [term], columnIndexToSearch: column.index }], {
            approximate: true,
            highlight: true,
        });
    }
    searchJurisdictionHeaders(terms, fireEvent = true) {
        const headers = [];
        for (let i = 0; i < this.flattened_headers.length; i++) {
            const header = this.flattened_headers[i];
            if (!header.isJurisdiction)
                continue;
            if (terms.length === 0 || terms.includes(header.title.toLowerCase())) {
                if (header.drawerButton?.isDrawerOpen) {
                    this.showColumnsWithoutAnimation(header.columnBlock?.columns.map(x => x.index) ?? [], fireEvent);
                }
                else {
                    this.showColumnsWithoutAnimation([header.columnBlock.columns[1].index, header.columnBlock.columns[2].index] ?? [], fireEvent);
                }
                headers.push(header);
            }
            else {
                this.hideColumnsWithoutAnimation(header.columnBlock?.columns.map((column) => column.index) ?? [], fireEvent);
            }
        }
        return headers;
    }
    updateRow(rowIndexOrRowId, newRowData) {
        const row = this.getRow(rowIndexOrRowId);
        if (!row)
            throw new Error(`Row with index or rowId ${rowIndexOrRowId} does not exist!`);
        const length = newRowData.length;
        let cellIndex = 0;
        for (let i = 0; i < length; i++) {
            const cellData = newRowData[i];
            if (typeof cellData === 'object') {
                const { data, link } = cellData;
                const cellBlock = row.cells[cellIndex].parentCellBlock;
                cellBlock.setExternalEditLink(link);
                if (this.symbol_map[data[0]]) {
                    this.setCellBlockToSymbol(row.localIndex, cellIndex, data[0]);
                    cellIndex += cellBlock.parentColumnBlock.columns.length;
                }
                else {
                    this.setCellBlockToSymbol(row.localIndex, cellIndex, '');
                    for (let j = 0; j < data.length; j++) {
                        this.updateCell(row.localIndex, cellIndex, data[j]);
                        cellIndex++;
                    }
                }
            }
            else {
                this.updateCell(row.localIndex, cellIndex, cellData);
                cellIndex++;
            }
        }
    }
    getCell(rowIndex, colIndex) {
        return this.rows[rowIndex].cells[colIndex];
    }
    updateCell(rowIndex, colIndex, data, fireEvent = false) {
        const cell = this.getCell(rowIndex, colIndex);
        cell.setData(data, fireEvent);
    }
    setCellBlockToSymbol(rowIndex, colIndex, symbolIdentifer) {
        const cellBlock = this.getCell(rowIndex, colIndex).parentCellBlock;
        const symbol = this.symbol_map[symbolIdentifer];
        cellBlock.cells.forEach((cell) => {
            cell.setData('', false);
        });
        cellBlock.symbolElement?.remove();
        if (symbol) {
            cellBlock.symbolElement = this.#createSymbolElement(symbol);
            cellBlock.element.prepend(cellBlock.symbolElement);
        }
        else {
            cellBlock.symbolElement = undefined;
        }
    }
    showColumns(columns, fireEvent = true) {
        this.is_column_transitioning = true;
        const tl = anime_es_js_1.default.timeline({
            complete: () => {
                this.showColumnsWithoutAnimation(columns, fireEvent);
                this.is_column_transitioning = false;
            },
        });
        let col;
        for (let i = 0; i < columns.length; i++) {
            if (typeof columns[i] === 'number') {
                const colIndex = columns[i];
                col = this.columns[colIndex];
            }
            else {
                col = columns[i];
            }
            const indexToAttachBefore = col.findIndexToInsertBefore();
            const targets = [];
            indexToAttachBefore !== -1 ? this.columns[indexToAttachBefore].element.before(col.element) : col.parentColumnBlock.element.appendChild(col.element);
            for (let rowIndex = this.starting_row.localIndex; rowIndex <= this.ending_row.localIndex; rowIndex++) {
                const row = this.rows[rowIndex];
                const cell = row.cells[col.index];
                indexToAttachBefore !== -1 ? row.cells[indexToAttachBefore].element.before(cell.element) : cell.parentCellBlock.element.appendChild(cell.element);
                targets.push(cell.element);
            }
            targets.push(col.element);
            tl.add({
                targets: targets,
                opacity: [0, 1],
                paddingLeft: ['0px', RetentionSchedule.COLUMN_PADDING_LEFT],
                paddingRight: ['0px', RetentionSchedule.COLUMN_PADDING_RIGHT],
                width: ['0px', col.width],
                duration: RetentionSchedule.COLUMN_ANIMATION_DURATION,
                easing: 'easeInOutQuad',
            }, 0);
        }
        tl.play();
    }
    hideColumns(columns, fireEvent = true) {
        this.is_column_transitioning = true;
        {
            let col;
            for (let i = 0; i < columns.length; i++) {
                if (typeof columns[i] === 'number') {
                    const colIndex = columns[i];
                    col = this.columns[colIndex];
                }
                else {
                    col = columns[i];
                }
                col.setArbitaryVisibility(false);
            }
        }
        this.#setEndingColumnBlock();
        this.#renderViewport();
        const targets = [];
        {
            let col;
            for (let i = 0; i < columns.length; i++) {
                if (typeof columns[i] === 'number') {
                    const colIndex = columns[i];
                    col = this.columns[colIndex];
                }
                else {
                    col = columns[i];
                }
                targets.push(col.element);
                for (let rowIndex = this.starting_row.localIndex; rowIndex <= this.ending_row.localIndex; rowIndex++) {
                    const row = this.rows[rowIndex];
                    let colIndex = col.index;
                    const cell = row.cells[colIndex];
                    targets.push(cell.element);
                }
            }
        }
        const a = (0, anime_es_js_1.default)({
            targets: targets,
            opacity: 0,
            paddingLeft: 0,
            paddingRight: 0,
            duration: RetentionSchedule.COLUMN_ANIMATION_DURATION,
            easing: 'easeInOutQuad',
            width: 0,
            complete: () => {
                this.hideColumnsWithoutAnimation(columns, fireEvent);
                a.reset();
                this.is_column_transitioning = false;
            }
        });
    }
    hideColumnsWithoutAnimation(columns, fireEvent = true) {
        const totalRows = this.global_rows.length;
        let col;
        for (let i = 0; i < columns.length; i++) {
            if (typeof columns[i] === 'number') {
                const colIndex = columns[i];
                col = this.columns[colIndex];
            }
            else {
                col = columns[i];
            }
            const colIndex = col.index;
            col.setArbitaryVisibility(false);
            col.element.remove();
            for (let rowIndex = 0; rowIndex < totalRows; rowIndex++) {
                const row = this.global_rows[rowIndex];
                row.cells[colIndex].element.remove();
            }
            if (fireEvent) {
                this.events['columnVisibilityChange'].call(this, { column: col });
            }
        }
        setTimeout(() => {
            this.#renderCurrentViewport();
        }, 0);
    }
    showColumnsWithoutAnimation(columns, fireEvent = true) {
        const totalRows = this.global_rows.length;
        let col;
        for (let i = 0; i < columns.length; i++) {
            if (typeof columns[i] === 'number') {
                const colIndex = columns[i];
                col = this.columns[colIndex];
            }
            else {
                col = columns[i];
            }
            col.setArbitaryVisibility(true);
            const indexToAttachBefore = col.findIndexToInsertBefore();
            indexToAttachBefore !== -1 ? this.columns[indexToAttachBefore].element.before(col.element) : col.parentColumnBlock.element.appendChild(col.element);
            for (let rowIndex = 0; rowIndex < totalRows; rowIndex++) {
                const row = this.global_rows[rowIndex];
                const cell = row.cells[col.index];
                indexToAttachBefore !== -1 ? row.cells[indexToAttachBefore].element.before(cell.element) : cell.parentCellBlock.element.appendChild(cell.element);
            }
            if (fireEvent) {
                this.events['columnVisibilityChange'].call(this, { column: col });
            }
        }
        setTimeout(() => {
            this.#renderCurrentViewport();
        }, 0);
    }
    toggleColumn(col, isVisible) {
        if (isVisible) {
            this.showColumns([col]);
        }
        else {
            this.hideColumns([col]);
        }
    }
    addColumn(columnData, columnBlockIndex, localIndex) {
        const columnBlock = this.column_blocks[columnBlockIndex];
        const colToAddBefore = columnBlock.columns[localIndex];
        const globalIndex = colToAddBefore ? colToAddBefore.index : columnBlock.columns.length;
        let { title, hidden, isEditable, width, uuid, payload, description } = columnData;
        if (width === undefined)
            width = RetentionSchedule.COLUMN_DEFAULT_WIDTH;
        if (hidden === undefined)
            hidden = false;
        if (isEditable === undefined)
            isEditable = 'plaintext';
        const newColumn = this.#createColumn(columnBlock, title, hidden, width, isEditable, uuid, payload, description);
        if (colToAddBefore) {
            columnBlock.columns.splice(localIndex, 0, newColumn);
            columnBlock.element.insertBefore(newColumn.element, colToAddBefore.element);
        }
        else {
            columnBlock.columns.push(newColumn);
            columnBlock.element.appendChild(newColumn.element);
        }
        columnBlock.width += newColumn.isVisible ? newColumn.width : 0;
        this.columns.splice(globalIndex, 0, newColumn);
        for (let i = 0; i < this.global_rows.length; i++) {
            const row = this.global_rows[i];
            const cellBlock = row.cellBlocks[columnBlock.index];
            const cell = this.#createCell(cellBlock, newColumn, false, '', globalIndex, width);
            row.cells.splice(globalIndex, 0, cell);
            if (colToAddBefore)
                cellBlock.element.insertBefore(cell.element, row.cells[globalIndex + 1].element);
            else
                cellBlock.element.appendChild(cell.element);
            row.cells.forEach((cell, i) => {
                cell.setIndex(i);
            });
        }
        this.columns.forEach((col, i) => {
            col.setIndex(i);
        });
        columnBlock.columns.forEach((col, i) => {
            col.setLocalIndex(i);
        });
        this.#renderCurrentViewport();
        return newColumn;
    }
    deleteColumn(col) {
        let colIndex;
        if (typeof col === 'number') {
            colIndex = col;
            col = this.columns[col];
        }
        else if (typeof col === 'string') {
            let uuid = col;
            colIndex = this.columns.findIndex(x => x.uuid === uuid);
            col = this.columns[colIndex];
        }
        else {
            colIndex = col.index;
        }
        col.element.remove();
        this.columns.splice(colIndex, 1);
        const columnBlock = col.parentColumnBlock;
        columnBlock.columns.splice(columnBlock.columns.findIndex((col) => col.index === colIndex), 1);
        columnBlock.width -= col.isVisible ? col.width : 0;
        for (let i = 0; i < this.global_rows.length; i++) {
            const row = this.global_rows[i];
            row.cells[colIndex].element.remove();
            row.cells.splice(colIndex, 1);
            row.cells.forEach((cell, i) => {
                cell.setIndex(i);
            });
        }
        this.columns.forEach((col, i) => {
            col.setIndex(i);
        });
        columnBlock.columns.forEach((col, i) => {
            col.setLocalIndex(i);
        });
        this.#renderCurrentViewport();
    }
    clear() {
        this.#initDOMStructure();
        this.#initSkeleton();
    }
    setData({ data, headers }) {
        this.#setHeaders(headers);
        this.#setData(data);
    }
    setBodyData(data) {
        this.#setData(data);
    }
    getData() {
        let data = [];
        const parseHeaderData = (header) => {
            const cellBlocks = this.global_rows.map((row) => row.cellBlocks[header.index]);
            return {
                title: header.title,
                columns: header.columnBlock?.columns.map((column) => {
                    if (header.isJurisdiction) {
                        return {
                            title: column.title,
                            isVisible: header.isVisible,
                        };
                    }
                    else {
                        return {
                            title: column.title,
                            isVisible: column.isVisible,
                        };
                    }
                }) ?? [],
                data: cellBlocks.map((cellBlock) => {
                    if (!cellBlock.symbolElement) {
                        return cellBlock.cells.map((cell) => {
                            const col = this.columns[cell.index];
                            if (col.dropdownItems)
                                return col.dropdownItems.get(cell.data);
                            return cell.data;
                        });
                    }
                    else {
                        return [{
                                value: cellBlock.symbolElement.dataset.value,
                            }];
                    }
                })
            };
        };
        this.headers.forEach((header) => {
            if (!header.subHeaders) {
                data.push(parseHeaderData(header));
            }
            else {
                data.push({
                    title: header.title,
                    subheaders: header.subHeaders.map((subHeader) => {
                        return parseHeaderData(subHeader);
                    })
                });
            }
        });
        return data;
    }
    async #generateWorkbook(id, logoURL, columnWidths = {}) {
        return new Promise((resolve) => {
            setTimeout(async () => {
                const defaultFileName = `Retention Schedule - ${id}`;
                const headerData = this.getData();
                const globalHiddenRowSetIndexes = new Set(this.global_rows.filter((row) => row.localIndex === -1).map(x => x.globalIndex));
                const totalDataColumns = headerData.reduce((acc, header) => {
                    if (header.subheaders) {
                        return acc + header.subheaders.reduce((acc, subHeader) => {
                            return acc + subHeader.columns.length;
                        }, 0);
                    }
                    else {
                        return acc + header.columns.length;
                    }
                }, 0);
                const bordergrey = 'fff2f2f2';
                const totalWorksheetColumns = totalDataColumns + 2;
                const workbook = new exceljs_1.default.Workbook();
                const worksheet = workbook.addWorksheet(defaultFileName, {
                    properties: {
                        tabColor: {
                            argb: 'FF00FF00',
                        }
                    }
                });
                worksheet.addRow(Array(totalWorksheetColumns).fill(''));
                const firstRow = worksheet.getRow(1);
                firstRow.eachCell((cell) => {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'ffffffff' },
                    };
                });
                firstRow.height = 50;
                const imageResponse = await fetch(logoURL);
                const imageBuffer = await imageResponse.arrayBuffer();
                const imageId = workbook.addImage({
                    buffer: imageBuffer,
                    extension: 'png',
                });
                worksheet.addImage(imageId, {
                    tl: { col: 1, row: 0.4 },
                    ext: { width: 25 * 2.7519379845 * 2, height: 25 * 2 }
                });
                let sheetContainsSubHeaders = false;
                for (let i = 0; i < headerData.length; i++) {
                    if (headerData[i].subheaders) {
                        sheetContainsSubHeaders = true;
                        break;
                    }
                }
                let bodyGapAdded = false;
                const defaultColumnWidth = 18;
                const processHeader = (currentRow, currentRowIndex, currentCol, header, headerIndex) => {
                    currentRow.getCell(currentCol).value = header.title;
                    currentRow.getCell(currentCol).alignment = { horizontal: 'center', vertical: 'middle' };
                    currentRow.getCell(currentCol).fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: {
                            argb: header.title.toLowerCase() === 'golden standard' ? 'fff9f078' : 'ff37a29a'
                        },
                    };
                    currentRow.getCell(currentCol).font = {
                        name: 'Lato Black',
                        color: { argb: header.title.toLowerCase() === 'golden standard' ? 'ff000000' : 'ffffffff' },
                        size: 10,
                        bold: true,
                    };
                    currentRow.getCell(currentCol).border = {
                        top: { style: 'thin', color: { argb: 'ff000000' } },
                        bottom: { style: 'thin', color: { argb: 'ff000000' } },
                    };
                    if (headerIndex === 0) {
                        currentRow.getCell(currentCol).border = {
                            ...currentRow.getCell(currentCol).border,
                            left: { style: 'thin', color: { argb: 'ff000000' } },
                        };
                    }
                    if (headerIndex === headerData.length - 1) {
                        currentRow.getCell(currentCol).border = {
                            ...currentRow.getCell(currentCol).border,
                            right: { style: 'thin', color: { argb: 'ff000000' } },
                        };
                    }
                    if (headerData[headerIndex + 1]?.title.toLowerCase() === 'golden standard') {
                        currentRow.getCell(currentCol).border = {
                            ...currentRow.getCell(currentCol).border,
                            right: { style: 'thin', color: { argb: 'ff000000' } },
                        };
                    }
                    if (headerData[headerIndex - 1]?.title.toLowerCase() === 'golden standard') {
                        currentRow.getCell(currentCol).border = {
                            ...currentRow.getCell(currentCol).border,
                            left: { style: 'thin', color: { argb: 'ff000000' } },
                        };
                    }
                    currentRow = worksheet.getRow(++currentRowIndex);
                    currentRow.height = 30;
                    if (header.columns) {
                        header.columns.forEach((column, i) => {
                            const { title: columnTitle, isVisible: isColumnVisible } = column;
                            currentRow.getCell(currentCol + i).value = columnTitle;
                            currentRow.getCell(currentCol + i).alignment = { horizontal: 'center', vertical: 'middle' };
                            currentRow.getCell(currentCol + i).fill = {
                                type: 'pattern',
                                pattern: 'solid',
                                fgColor: { argb: 'ffffffff' },
                            };
                            currentRow.getCell(currentCol + i).font = {
                                name: 'Lato Black',
                                color: { argb: 'ff000000' },
                                size: 8,
                            };
                            currentRow.getCell(currentCol + i).border = {
                                top: { style: 'thin', color: { argb: 'ff000000' } },
                                bottom: { style: 'thin', color: { argb: 'ff000000' } },
                            };
                            if (i === 0 && !(headerData[headerIndex - 1]?.title.toLowerCase() === 'your data' && header.title.toLowerCase() === 'tell us what it is')) {
                                currentRow.getCell(currentCol + i).border = {
                                    ...currentRow.getCell(currentCol + i).border,
                                    left: { style: 'thin', color: { argb: 'ff000000' } },
                                };
                            }
                            if (i === header.columns.length - 1 && !(headerData[headerIndex + 1]?.title.toLowerCase() === 'tell us what it is' && header.title.toLowerCase() === 'your data')) {
                                currentRow.getCell(currentCol + i).border = {
                                    ...currentRow.getCell(currentCol + i).border,
                                    right: { style: 'thin', color: { argb: 'ff000000' } },
                                };
                            }
                            if (header.title.toLowerCase() === 'golden standard') {
                                currentRow.getCell(currentCol + i).fill = {
                                    type: 'pattern',
                                    pattern: 'solid',
                                    fgColor: { argb: bordergrey },
                                };
                            }
                            worksheet.getColumn(currentCol + i).width = columnWidths[columnTitle.toLowerCase()] ?? defaultColumnWidth;
                            worksheet.getColumn(currentCol + i).hidden = !isColumnVisible;
                        });
                        currentRow = worksheet.getRow(++currentRowIndex);
                        if (!bodyGapAdded) {
                            currentRow.values = Array(totalWorksheetColumns).fill('');
                            currentRow.height = 10;
                            currentRow.eachCell((cell) => {
                                cell.fill = {
                                    type: 'pattern',
                                    pattern: 'solid',
                                    fgColor: { argb: bordergrey },
                                };
                            });
                            bodyGapAdded = true;
                        }
                        header.data.forEach((row, rowIndex) => {
                            currentRow = worksheet.getRow(++currentRowIndex);
                            currentRow.height = 70;
                            let symbolText = '';
                            header.columns.forEach((_, i) => {
                                if (symbolText === '') {
                                    if (typeof row[i] === 'object') {
                                        symbolText = row[i].value;
                                    }
                                    else if (/<a\b[^>]*>.*<\/a>/.test(row[i])) {
                                        let link = row[i].match(/href="([^"]*)/)[1];
                                        let text = row[i].match(/>([^<]*)/)[1];
                                        currentRow.getCell(currentCol + i).value = { text: link, hyperlink: link };
                                    }
                                    else
                                        currentRow.getCell(currentCol + i).value = row[i];
                                }
                                currentRow.getCell(currentCol + i).alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                                currentRow.getCell(currentCol + i).font = {
                                    name: 'Lato Regular',
                                    color: { argb: 'ff000000' },
                                    size: 8,
                                };
                                currentRow.getCell(currentCol + i).border = {
                                    bottom: { style: 'thick', color: { argb: bordergrey } },
                                };
                                if (i === header.columns.length - 1) {
                                    currentRow.getCell(currentCol + i).border = {
                                        bottom: { style: 'thick', color: { argb: bordergrey } },
                                        right: { style: 'thick', color: { argb: bordergrey } },
                                    };
                                    if (headerData[headerData.indexOf(header) + 1]?.title.toLowerCase() === 'golden standard') {
                                        currentRow.getCell(currentCol + i).border = {
                                            ...currentRow.getCell(currentCol + i).border,
                                            right: { style: 'thin', color: { argb: 'ff000000' } },
                                        };
                                    }
                                }
                                if (header.title.toLowerCase() === 'golden standard') {
                                    currentRow.getCell(currentCol + i).fill = {
                                        type: 'pattern',
                                        pattern: 'solid',
                                        fgColor: { argb: bordergrey },
                                    };
                                    currentRow.getCell(currentCol + i).border = {
                                        bottom: { style: 'thick', color: { argb: 'ffffffff' } },
                                        left: { style: 'thin', color: { argb: 'ffffffff' } },
                                    };
                                    if (i === header.columns.length - 1) {
                                        currentRow.getCell(currentCol + i).border = {
                                            ...currentRow.getCell(currentCol + i).border,
                                            right: { style: 'thin', color: { argb: 'ff000000' } },
                                        };
                                    }
                                }
                            });
                            if (symbolText !== '') {
                                worksheet.mergeCells(currentRowIndex, currentCol, currentRowIndex, currentCol + header.columns.length - 1);
                                currentRow.getCell(currentCol).value = symbolText;
                                currentRow.getCell(currentCol).alignment = { horizontal: 'center', vertical: 'middle' };
                                currentRow.getCell(currentCol).fill = {
                                    type: 'pattern',
                                    pattern: 'solid',
                                    fgColor: {
                                        argb: 'ffffffff'
                                    },
                                };
                                currentRow.getCell(currentCol).font = {
                                    name: 'Lato Black',
                                    color: { argb: 'ff000000' },
                                    size: 10,
                                    bold: true,
                                };
                                currentRow.getCell(currentCol).border = {
                                    bottom: { style: 'thick', color: { argb: bordergrey } },
                                    right: { style: 'thick', color: { argb: bordergrey } },
                                };
                            }
                            if (globalHiddenRowSetIndexes.has(rowIndex)) {
                                worksheet.getRow(currentRowIndex).hidden = true;
                            }
                        });
                    }
                };
                const createOuterBorder = (worksheet, start = { row: 1, col: 1 }, end = { row: 1, col: 1 }) => {
                    const borderStyle = {
                        style: 'thin',
                        color: { argb: 'ff000000' }
                    };
                    for (let i = start.row; i <= end.row; i++) {
                        const leftBorderCell = worksheet.getCell(i, start.col);
                        const rightBorderCell = worksheet.getCell(i, end.col);
                        leftBorderCell.border = {
                            ...leftBorderCell.border,
                            left: borderStyle
                        };
                        rightBorderCell.border = {
                            ...rightBorderCell.border,
                            right: borderStyle
                        };
                    }
                    for (let i = start.col; i <= end.col; i++) {
                        const topBorderCell = worksheet.getCell(start.row, i);
                        const bottomBorderCell = worksheet.getCell(end.row, i);
                        topBorderCell.border = {
                            ...topBorderCell.border,
                            top: borderStyle
                        };
                        bottomBorderCell.border = {
                            ...bottomBorderCell.border,
                            bottom: borderStyle
                        };
                    }
                };
                let currentCol = 2;
                headerData.forEach((header, headerIndex) => {
                    let currentRowIndex = 2;
                    let currentRow = worksheet.getRow(currentRowIndex);
                    currentRow.height = 30;
                    if (!header.subheaders) {
                        currentRow.getCell(currentCol).value = header.title;
                        if (sheetContainsSubHeaders) {
                            worksheet.mergeCells(currentRowIndex, currentCol, currentRowIndex + 1, currentCol + header.columns.length - 1);
                            currentRow = worksheet.getRow(++currentRowIndex);
                            currentRow.height = 30;
                        }
                        else
                            worksheet.mergeCells(currentRowIndex, currentCol, currentRowIndex, currentCol + header.columns.length - 1);
                        processHeader(currentRow, currentRowIndex, currentCol, header, headerIndex);
                        currentCol += header.columns.length;
                    }
                    else {
                        const cols = (header.subheaders.length * 7);
                        worksheet.mergeCells(currentRowIndex, currentCol, currentRowIndex, currentCol + cols - 1);
                        processHeader(currentRow, currentRowIndex, currentCol, header, headerIndex);
                        const x = currentRow.getCell(currentCol).border;
                        delete x.bottom;
                        currentRow.getCell(currentCol).border = x;
                        currentRow = worksheet.getRow(++currentRowIndex);
                        currentRow.height = 30;
                        header.subheaders.forEach((subheader, subheaderIndex) => {
                            worksheet.mergeCells(currentRowIndex, currentCol, currentRowIndex, currentCol + subheader.columns.length - 1);
                            processHeader(currentRow, currentRowIndex, currentCol, subheader, headerIndex);
                            currentRow.getCell(currentCol).fill = {
                                type: 'pattern',
                                pattern: 'solid',
                                fgColor: {
                                    argb: 'ff5ac4bc'
                                },
                            };
                            const x = currentRow.getCell(currentCol).border;
                            delete x.top;
                            if (!(subheaderIndex === header.subheaders.length - 1 && headerIndex === headerData.length - 1)) {
                                delete x.right;
                            }
                            delete x.left;
                            currentRow.getCell(currentCol).border = x;
                            currentCol += subheader.columns.length;
                        });
                    }
                });
                if (sheetContainsSubHeaders) {
                    createOuterBorder(worksheet, { row: 6, col: 2 }, { row: worksheet.rowCount, col: worksheet.columnCount - 1 });
                }
                else {
                    createOuterBorder(worksheet, { row: 5, col: 2 }, { row: worksheet.rowCount, col: worksheet.columnCount - 1 });
                }
                const firstColumnCells = worksheet.getColumn(1);
                firstColumnCells.eachCell((cell) => {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: {
                            argb: 'ffffffff'
                        },
                    };
                });
                const lastColumnCells = worksheet.getColumn(worksheet.columnCount);
                lastColumnCells.eachCell((cell) => {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: {
                            argb: 'ffffffff'
                        },
                    };
                });
                const lastRow = worksheet.addRow(Array(totalWorksheetColumns).fill(''));
                lastRow.height = 80;
                lastRow.eachCell((cell) => {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'ffffffff' },
                    };
                });
                worksheet.views = [
                    { state: 'frozen', ySplit: sheetContainsSubHeaders ? 3 : 2 },
                ];
                const lastRowCell = lastRow.getCell(2);
                lastRowCell.value = `© ${new Date().getFullYear()} - filerskeepers B.V. - license terms apply - www.filerskeepers.co`;
                lastRowCell.font = {
                    name: 'Lato Regular',
                    color: { argb: 'ff000000' },
                    size: 8,
                };
                lastRowCell.alignment = { horizontal: 'left', vertical: 'middle' };
                resolve({ workbook, defaultFileName });
            }, 0);
        });
    }
    exportToExcelAndDownload = (id, logoURL, columnWidths = {}) => {
        return new Promise((resolve) => {
            this.#generateWorkbook(id, logoURL, columnWidths).then(({ workbook, defaultFileName }) => {
                resolve('Excel file generated. Downloading...');
                workbook.xlsx.writeBuffer().then(function (buffer) {
                    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = defaultFileName + '.xlsx';
                    a.style.display = 'none';
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                });
            });
        });
    };
    constructor({ selector, scrollContainer = '#main_container', symbolMap, canEditCells = true, canAddRows = true, canDeleteRows = true, canMoveRows = true }) {
        this.scroll_container = document.querySelector(scrollContainer);
        this.symbol_map = symbolMap ?? {};
        this.can_edit_cells = canEditCells;
        this.can_add_rows = canAddRows;
        this.can_delete_rows = canDeleteRows;
        this.can_move_rows = canMoveRows;
        this.#appendTableToDOM(selector);
        this.#initDOMStructure();
        this.#initSkeleton();
        this.#initCellEditButton();
        this.#initCellEditDropdown();
        this.#initListeners();
    }
    #setHeaders(headers) {
        this.#clearInnerVariablesHead();
        this.#initHead(headers);
    }
    #createInvisibleCell(cell) {
        const element = cell.element.cloneNode(true);
        element.textContent = '';
        element.classList.add('invisible-cell');
        return element;
    }
    #createInvisibleColumn(col) {
        const element = col.element.cloneNode(true);
        element.textContent = '';
        element.classList.add('invisible');
        return element;
    }
    #setData(bodyData) {
        this.#clearInnerVariablesBody();
        this.body_offset_top = this.body.offsetTop;
        let numOfRows = bodyData.length;
        let numOfColumnBlocks = this.column_blocks.length;
        {
            if (this.DEBUG_MODE)
                console.time('loop');
            this.DEBUG_MODE &&
                console.time('bodyLoop');
            for (let rowIndex = 0; rowIndex < numOfRows; rowIndex++) {
                const cells = [], cellBlocks = [];
                let dataIndex = 0, rowId = bodyData[rowIndex][0], rowData = bodyData[rowIndex][1];
                const row = this.#createRow(rowIndex, rowId);
                row.setTranslate({
                    vTranslate: RetentionSchedule.INTRINSIC_ROW_HEIGHT * rowIndex,
                    hTranslate: this.rows_h_translate,
                });
                let isSymbol = false;
                for (let j = 0; j < numOfColumnBlocks; j++) {
                    const columnBlock = this.column_blocks[j];
                    let numOfColumns = columnBlock.columns.length;
                    let cellData = rowData[dataIndex];
                    const cellBlock = this.#createCellBlock(columnBlock, j, row);
                    cellBlocks.push(cellBlock);
                    if (columnBlock.isJurisdiction) {
                        let { data, link } = cellData;
                        cellBlock.setExternalEditLink(link);
                        isSymbol = this.symbol_map[data[0]] ? true : false;
                        if (isSymbol) {
                            cellBlock.symbolElement = this.#createSymbolElement(this.symbol_map[data[0]]);
                            cellBlock.element.prepend(cellBlock.symbolElement);
                        }
                        for (let k = 0; k < numOfColumns; k++) {
                            const col = columnBlock.columns[k];
                            const cellData = data[k];
                            const cell = this.#createCell(cellBlock, col, isSymbol, cellData, cells.length, col.width);
                            row.calculationElement.appendChild(cell.calculationElement);
                            if (col.isVisible)
                                cellBlock.element.appendChild(cell.element);
                            cellBlock.cells.push(cell);
                            cells.push(cell);
                        }
                        dataIndex++;
                    }
                    else {
                        for (let k = 0; k < numOfColumns; k++) {
                            const col = columnBlock.columns[k];
                            const cellData = rowData[dataIndex];
                            const cell = this.#createCell(cellBlock, col, isSymbol, cellData, cells.length, col.width);
                            row.calculationElement.appendChild(cell.calculationElement);
                            if (col.isVisible)
                                cellBlock.element.appendChild(cell.element);
                            cellBlock.cells.push(cell);
                            cells.push(cell);
                            dataIndex++;
                        }
                    }
                }
                row.cells = cells;
                row.cellBlocks = cellBlocks;
                this.rows.push(row);
                row.setGlobalIndex(this.global_rows.length);
                this.global_rows.push(row);
            }
            this.DEBUG_MODE &&
                console.timeEnd('bodyLoop');
            this.#setBodyHeightAndAdjustPinnedContainer(RetentionSchedule.INTRINSIC_ROW_HEIGHT * numOfRows);
        }
        this.#renderCurrentViewport();
        this.body.classList.remove('animate-in');
        this.body.classList.add('animate-in');
        this.drawer_buttons.forEach((drawerButton) => {
            drawerButton.show(this.starting_row?.height && this.starting_row?.height < 500 ? this.starting_row?.height : 500);
        });
        if (this.DEBUG_MODE)
            console.timeEnd('loop');
    }
    #createPinnedRowElement(row) {
        const div = document.createElement('div');
        div.classList.add('row');
        div.style.height = `${row.height}px`;
        div.style.transform = `translateX(${0}px) translateY(${row.vTranslate}px)`;
        return div;
    }
    #clearInnerVariablesHead() {
        this.headers = [];
        this.flattened_headers = [];
        this.column_blocks = [];
        this.columns = [];
        this.drawer_buttons = [];
        this.starting_column_block = undefined;
        this.ending_column_block = undefined;
        this.is_column_transitioning = false;
        this.currently_opened_header_dropdown = undefined;
        this.currently_hovered_row = undefined;
        this.cell_edit_dropdown.remove();
        this.cell_edit_button.remove();
        this.add_row_green_area.remove();
    }
    #clearInnerVariablesBody() {
        this.global_rows = [];
        this.rows = [];
        this.body_height = 0;
        this.starting_row = undefined;
        this.ending_row = undefined;
        this.rows_h_translate = 0;
        this.body_offset_top = 0;
        this.currently_hovered_row = undefined;
    }
    #initDOMStructure() {
        this.wrapper.innerHTML = '';
        this.head.innerHTML = '';
        this.body.innerHTML = '';
        this.body.removeAttribute('style');
        this.render_container.innerHTML = '';
        this.body_skeleton.innerHTML = '';
        this.wrapper.classList.add('retention-schedule-wrapper');
        this.row_processor.classList.add('row-processor');
        this.head.classList.add('headers');
        this.body.classList.add('body');
        this.render_container.classList.add('render-container');
        this.pinned_column_container.wrapper.classList.add('pinned-column-container');
        this.pinned_column_container.renderContainer.classList.add('render-container');
        this.pinned_column_container.header.classList.add('pinned-headers');
        this.no_data_wrapper.classList.add('retention-schedule-no-data');
        this.no_data_wrapper.innerHTML = `<diV>
			No data to display
		</diV>
		<button><span>refresh</span>Reset schedule</button>
		`;
        this.row_number_container.classList.add('row-number-container');
        this.pinned_column_container.wrapper.appendChild(this.pinned_column_container.header);
        this.pinned_column_container.wrapper.appendChild(this.pinned_column_container.renderContainer);
        this.body.appendChild(this.render_container);
        this.wrapper.appendChild(this.row_processor);
        this.wrapper.appendChild(this.head);
        this.wrapper.appendChild(this.body);
        this.wrapper.appendChild(this.pinned_column_container.wrapper);
    }
    #initSkeleton() {
        this.wrapper.classList.remove('headers-loaded');
        this.wrapper.appendChild(this.body_skeleton);
        const skeleton = this.body_skeleton;
        skeleton.classList.add('skeleton');
        const headerSkeleton = document.createElement('div');
        headerSkeleton.classList.add('header-skeleton');
        skeleton.appendChild(headerSkeleton);
        for (let i = 0; i < 4; i++) {
            const item = document.createElement('div');
            item.appendChild(document.createElement('div'));
            headerSkeleton.appendChild(item);
        }
        const bodySkeleton = document.createElement('div');
        bodySkeleton.classList.add('body-skeleton');
        const container = document.createElement('div');
        bodySkeleton.appendChild(container);
        ['60%', '40%', '80%', '100%', '60%', '40%', '80%', '100%', '60%', '40%', '80%', '100%', '60%', '40%', '80%', '100%'].forEach((width) => {
            const line = document.createElement('div');
            line.style.width = width;
            container.appendChild(line);
        });
        skeleton.appendChild(bodySkeleton);
        const loadingDiv = this.loading_div;
        loadingDiv.classList.add('loading');
        this.loading_icon.classList.add('loading-icon');
        this.loading_text.classList.add('loading-text');
        loadingDiv.appendChild(this.loading_icon);
        loadingDiv.appendChild(this.loading_text);
        skeleton.appendChild(loadingDiv);
        return new Promise((resolve) => {
            (0, anime_es_js_1.default)({
                targets: skeleton,
                opacity: 1,
                height: [0, 400],
                duration: 700,
                easing: 'easeInOutQuart',
                complete: () => {
                    setTimeout(() => {
                        resolve();
                    }, 300);
                }
            });
        });
    }
    #initCellEditButton() {
        this.add_row_green_area = document.createElement('div');
        let greenAreaWrapper = this.add_row_green_area;
        greenAreaWrapper.classList.add('green-area-wrapper');
        let greenArea = document.createElement('div');
        greenArea.classList.add('green-area');
        greenAreaWrapper.appendChild(greenArea);
        const cellEditButton = this.cell_edit_button;
        cellEditButton.classList.add('retention-schedule-cell-edit-button');
        cellEditButton.textContent = 'edit';
        cellEditButton.show = ({ row, cell, column, elementToPrependTo }) => {
            elementToPrependTo.prepend(cellEditButton);
            cellEditButton.currentCell = cell;
            cellEditButton.currentRow = row;
            cellEditButton.currentColumn = column;
            cellEditButton.style.opacity = '0';
            (0, anime_es_js_1.default)({
                targets: cellEditButton,
                opacity: 1,
                duration: 100,
            });
            if (cellEditButton.currentCell.parentCellBlock.externalEditLink) {
                cellEditButton.setAttribute('data-modal-trigger', cellEditButton.currentCell.parentCellBlock.externalEditLink);
            }
            else {
                cellEditButton.removeAttribute('data-modal-trigger');
            }
        };
        cellEditButton.hide = () => {
            cellEditButton.style.opacity = '0';
            cellEditButton.remove();
        };
        cellEditButton.onclick = (e) => {
            if (!cellEditButton.currentCell || !cellEditButton.currentRow || !cellEditButton.currentColumn || !cellEditButton.currentColumn.isEditable)
                return;
            if (cellEditButton.currentCell.parentCellBlock.externalEditLink) {
                return;
            }
            const { currentCell: cell, currentRow: row, currentColumn: column } = cellEditButton;
            cell.setIsEditing(true);
            cellEditButton.hide();
        };
        cellEditButton.onmouseenter = (e) => {
            if (this.currently_hovered_row?.controlContainerElement) {
                this.currently_hovered_row.controlContainerElement.classList.add('forcefully-transparent');
            }
        };
        cellEditButton.onmouseleave = (e) => {
            if (this.currently_hovered_row?.controlContainerElement) {
                this.currently_hovered_row.controlContainerElement.classList.remove('forcefully-transparent');
            }
        };
    }
    #initCellEditDropdown() {
        let dropdown = this.cell_edit_dropdown;
        dropdown.classList.add('retention-schedule-cell-edit-dropdown');
        let dropdownItemContainer = document.createElement('div');
        dropdownItemContainer.classList.add('retention-schedule-cell-edit-dropdown-item-container');
        dropdown.appendChild(dropdownItemContainer);
        dropdown.setControlElements = (element, currentCell) => {
            dropdown.activatingElement = element;
            dropdown.currentCell = currentCell;
        };
        dropdown.setData = (data, callback) => {
            const documentFrag = document.createDocumentFragment();
            let length = data.length;
            for (let i = 0; i < length; i++) {
                const { groupName, items } = data[i];
                if (groupName !== "") {
                    let groupLabel = document.createElement('div');
                    groupLabel.textContent = groupName;
                    groupLabel.classList.add('retention-schedule-cell-edit-dropdown-group-label');
                    documentFrag.appendChild(groupLabel);
                }
                if (Array.isArray(items)) {
                    items.forEach(({ id, text }) => {
                        let dropdownItem = document.createElement('div');
                        dropdownItem.classList.add('retention-schedule-cell-edit-dropdown-item');
                        dropdownItem.setAttribute('data-value', id);
                        dropdownItem.textContent = text;
                        dropdownItem.onclick = (e) => {
                            callback(e, dropdownItem);
                        };
                        documentFrag.appendChild(dropdownItem);
                    });
                }
                else
                    Object.entries(items).forEach(([key, value]) => {
                        let dropdownItem = document.createElement('div');
                        dropdownItem.classList.add('retention-schedule-cell-edit-dropdown-item');
                        dropdownItem.setAttribute('data-value', key);
                        dropdownItem.textContent = value;
                        dropdownItem.onclick = (e) => {
                            callback(e, dropdownItem);
                        };
                        documentFrag.appendChild(dropdownItem);
                    });
            }
            dropdownItemContainer.replaceChildren(documentFrag);
        };
        dropdown.open = () => {
            dropdown.activatingElement?.classList.add('dropdown-open');
            document.body.appendChild(dropdown);
            dropdown.isOpen = true;
            dropdown.setPosition();
            document.addEventListener('click', documentClickHandler);
            if (dropdown.onOpen)
                dropdown.onOpen();
        };
        dropdown.close = () => {
            dropdown.activatingElement?.classList.remove('dropdown-open');
            dropdown.remove();
            dropdown.isOpen = false;
            document.body.removeEventListener('click', documentClickHandler);
            if (dropdown.onClose)
                dropdown.onClose();
        };
        dropdown.setPosition = () => {
            dropdown.style.top = `${dropdown.activatingElement?.getBoundingClientRect().bottom}px`;
            dropdown.style.left = `${dropdown.activatingElement?.getBoundingClientRect().left}px`;
            dropdown.style.minWidth = `${dropdown.activatingElement?.offsetWidth}px`;
        };
        const documentClickHandler = (e) => {
            if (e.target === dropdown.activatingElement || dropdown.contains(e.target) || (e.target === this.cell_edit_button && this.cell_edit_button.currentCell === dropdown.currentCell)
                || e.target.closest('.ignore-retention-schedule-dropdown-close'))
                return;
            dropdown.close();
        };
    }
    #initListeners() {
        {
            this.body.addEventListener('copy', (e) => {
                if (window.getSelection()?.toString() !== "")
                    return;
                if (this.cells_selected.length === 0)
                    return;
                let text = this.cells_selected.map(cell => cell.element.textContent).join('\t');
                e.clipboardData?.setData('text/plain', text);
                window.toast('Copied to clipboard', {
                    type: 'success',
                });
                e.preventDefault();
            });
        }
        {
            document.addEventListener('click', (e) => {
                if (this.currently_opened_header_dropdown) {
                    if (e.target.dropdown === this.currently_opened_header_dropdown)
                        return;
                    this.currently_opened_header_dropdown.remove();
                    this.currently_opened_header_dropdown = undefined;
                }
            });
        }
        {
            let oldScrollTop = this.scroll_container.scrollTop;
            let oldScrollLeft = this.scroll_container.scrollLeft;
            this.scroll_container.addEventListener('scroll', () => {
                this.currently_hovered_row?.controlContainerElement.resetTop();
                this.cell_edit_dropdown.setPosition();
            });
            this.scroll_container.addEventListener('scroll', () => {
                const { scrollTop, scrollLeft } = this.scroll_container;
                this.is_scrolling_down = oldScrollTop < scrollTop;
                this.is_scrolling_right = oldScrollLeft < scrollLeft;
                oldScrollTop = scrollTop;
                oldScrollLeft = scrollLeft;
                requestAnimationFrame(() => {
                    if (!this.starting_column_block || !this.ending_column_block || !this.starting_row || !this.ending_row)
                        return;
                    const startingColumnBlockRect = this.starting_column_block.element.getBoundingClientRect();
                    const endingColumnBlockRect = this.ending_column_block.element.getBoundingClientRect();
                    const startingRowRect = this.starting_row.element.getBoundingClientRect();
                    const endingRowRect = this.ending_row.element.getBoundingClientRect();
                    const scrollContainerRect = this.scroll_container.getBoundingClientRect();
                    const numberOfRows = this.rows.length;
                    const appendCellBlocksRight = this.is_scrolling_right &&
                        endingColumnBlockRect.right < scrollContainerRect.right &&
                        this.ending_column_block.index < this.column_blocks.length - 1;
                    const appendCellBlocksLeft = !this.is_scrolling_right &&
                        startingColumnBlockRect.left > scrollContainerRect.left &&
                        this.starting_column_block.index > 0;
                    const removeCellBlocksRight = this.is_scrolling_right &&
                        startingColumnBlockRect.right < scrollContainerRect.left &&
                        this.starting_column_block.index < this.column_blocks.length - 1;
                    const removeCellBlocksLeft = !this.is_scrolling_right &&
                        endingColumnBlockRect.left > scrollContainerRect.right &&
                        this.ending_column_block.index > 0;
                    const renderRowsDown = this.is_scrolling_down &&
                        endingRowRect.bottom < scrollContainerRect.bottom &&
                        this.ending_row.localIndex < numberOfRows - 1;
                    const renderRowsUp = !this.is_scrolling_down &&
                        startingRowRect.top > scrollContainerRect.top &&
                        this.starting_row.localIndex > 0;
                    if (appendCellBlocksRight || appendCellBlocksLeft) {
                        const diff = appendCellBlocksRight ? scrollContainerRect.right - endingColumnBlockRect.right : startingColumnBlockRect.left - scrollContainerRect.left;
                        this.#appendCellBlocks(diff, appendCellBlocksRight);
                    }
                    if (removeCellBlocksRight || removeCellBlocksLeft) {
                        const diff = removeCellBlocksRight ? scrollContainerRect.left - startingColumnBlockRect.left : endingColumnBlockRect.right - scrollContainerRect.right;
                        this.#removeCellBlocks(diff, removeCellBlocksRight);
                    }
                    if (renderRowsDown || renderRowsUp) {
                        const diff = renderRowsDown ? scrollContainerRect.bottom - endingRowRect.bottom : startingRowRect.top - scrollContainerRect.top;
                        this.#displayRows(diff, renderRowsDown);
                    }
                });
            });
            this.scroll_container.addEventListener('scroll', () => {
                requestAnimationFrame(() => {
                    const scrollContainerRect = this.scroll_container.getBoundingClientRect();
                    const scrollX = this.scroll_container.scrollLeft;
                    const scrollContainerLeft = scrollContainerRect.left;
                    const scrollContainerRight = scrollContainerRect.right;
                    this.pinned_column_container.pinnedColumns.forEach((pinnedObj) => {
                        const columnRect = pinnedObj.originalColumn.element.getBoundingClientRect();
                        if (columnRect.left - pinnedObj.startingPinOffset < scrollContainerLeft && !pinnedObj.isBeingPinned) {
                            pinnedObj.isBeingPinned = true;
                            pinnedObj.pinOffset = scrollX - (scrollContainerLeft - columnRect.left);
                            pinnedObj.originalColumn.element.classList.add('pinned');
                            pinnedObj.element.classList.add('pinned');
                        }
                        if (columnRect.left - pinnedObj.startingPinOffset >= scrollContainerLeft && !pinnedObj.alreadyPinnedOnce) {
                            pinnedObj.alreadyPinnedOnce = true;
                        }
                        if (pinnedObj.currentLeft < 0 && pinnedObj.isBeingPinned) {
                            pinnedObj.isBeingPinned = false;
                            pinnedObj.pinOffset = pinnedObj.startingPinOffset;
                            pinnedObj.currentLeft = 0;
                            gsap.to([pinnedObj.originalColumn.element, pinnedObj.element], {
                                x: 0,
                                duration: 0,
                            });
                            pinnedObj.originalColumn.element.classList.remove('pinned');
                            pinnedObj.element.classList.remove('pinned');
                        }
                        if (pinnedObj.isBeingPinned) {
                            let l = scrollX - pinnedObj.pinOffset + pinnedObj.startingPinOffset;
                            pinnedObj.currentLeft = l;
                            gsap.to([pinnedObj.originalColumn.element, pinnedObj.element], {
                                x: l,
                                duration: !pinnedObj.alreadyPinnedOnce ? 0.3 : 0,
                                onComplete: () => {
                                    pinnedObj.alreadyPinnedOnce = true;
                                }
                            });
                        }
                    });
                });
            });
            let isTopPinned = false;
            this.scroll_container.addEventListener('scroll', () => {
                const scrollY = this.scroll_container.scrollTop;
                const scrollContainerRect = this.scroll_container.getBoundingClientRect();
                if (scrollContainerRect.top >= this.head.getBoundingClientRect().top && !isTopPinned) {
                    isTopPinned = true;
                }
                if (this.head.getBoundingClientRect().top > scrollContainerRect.top && isTopPinned) {
                    isTopPinned = false;
                    gsap.to(this.pinned_column_container.wrapper, {
                        y: 0,
                        duration: 0,
                        ease: 'none'
                    });
                    gsap.to(this.pinned_column_container.renderContainer, {
                        y: 0,
                        duration: 0,
                        ease: 'none'
                    });
                }
                if (isTopPinned) {
                    gsap.to(this.pinned_column_container.wrapper, {
                        y: scrollY - this.wrapper.offsetTop - 2.5,
                        duration: 0,
                        ease: 'none'
                    });
                    gsap.to(this.pinned_column_container.renderContainer, {
                        y: -(scrollY - this.wrapper.offsetTop - 2.5),
                        duration: 0,
                        ease: 'none'
                    });
                }
            });
        }
        {
            this.scroll_container.addEventListener('scrollend', () => {
                this.is_column_transitioning = false;
            });
        }
        {
            window.addEventListener('resize', (e) => {
                this.#renderCurrentViewport();
            });
        }
        {
            this.body.addEventListener("dragover", (event) => {
                event.preventDefault();
            });
        }
    }
    #initHead(headers) {
        this.wrapper.classList.add('headers-loaded');
        (0, anime_es_js_1.default)({
            targets: this.body_skeleton,
            opacity: 0,
            duration: 200,
            complete: () => {
                this.body_skeleton.remove();
            }
        });
        if (this.DEBUG_MODE)
            console.time('init head');
        this.body_skeleton.remove();
        let length = headers.length;
        for (let i = 0; i < length; i++) {
            let ele = headers[i];
            let header = this.#createHeader(ele, i);
            this.head.appendChild(header.element);
            this.headers.push(header);
            if (header.subHeaders) {
                this.flattened_headers.push(...header.subHeaders);
            }
            else {
                this.flattened_headers.push(header);
            }
        }
        this.flattened_headers.forEach((header, i) => {
            header.index = i;
            header.element.setAttribute('data-index', `${i}`);
        });
        this.pinned_column_container.offsetTop = this.head.getBoundingClientRect().height - this.columns[0].element.getBoundingClientRect().height;
        this.pinned_column_container.wrapper.style.top = `${this.pinned_column_container.offsetTop}px`;
        if (this.DEBUG_MODE)
            console.timeEnd('init head');
    }
    #createHeader(headerData, index, parentHeader) {
        const headerElement = document.createElement('div'), titleElement = document.createElement('div');
        let dropdownTrigger;
        headerElement.classList.add('header');
        titleElement.classList.add('title');
        headerElement.appendChild(titleElement);
        headerElement.setAttribute('data-index', `${index}`);
        headerElement.style.animationDelay = `${index * RetentionSchedule.HEADER_ENTRY_ANIMATION_DELAY}s`;
        const HEADER = {
            element: headerElement,
            titleElement: titleElement,
            title: headerData.title,
            index: index,
            payload: headerData.payload ?? {},
            isVisible: true,
            uuid: headerData.uuid ?? '',
            isJurisdiction: headerData.drawerColumns || headerData.subHeaders ? true : false,
            setTitle: (title) => {
                HEADER.titleElement.textContent = title;
                HEADER.title = title;
                if (dropdownTrigger) {
                    titleElement.appendChild(dropdownTrigger);
                }
            },
            setVisibility: (visible) => {
                if (visible) {
                    HEADER.element.classList.remove('hidden');
                    HEADER.isVisible = true;
                    if (HEADER.parentHeader) {
                        HEADER.parentHeader.setVisibility(true);
                    }
                }
                else {
                    HEADER.element.classList.add('hidden');
                    HEADER.isVisible = false;
                    if (HEADER.parentHeader) {
                        const parentHeader = HEADER.parentHeader;
                        let allSubHeadersHidden = parentHeader.subHeaders?.every((subHeader) => !subHeader.isVisible);
                        if (allSubHeadersHidden) {
                            parentHeader.setVisibility(false);
                        }
                    }
                }
            }
        };
        headerElement.rsObject = HEADER;
        if (HEADER.isJurisdiction) {
            HEADER.element.setAttribute('data-jurisdiction', 'true');
        }
        if (parentHeader) {
            HEADER.parentHeader = parentHeader;
            parentHeader.subHeaders?.push(HEADER);
        }
        if (headerData.subHeaders) {
            HEADER.subHeaders = [];
            headerElement.classList.add('has-sub-headers');
            let subheader_container = document.createElement('div');
            subheader_container.classList.add('sub-headers');
            let subHeaders = headerData.subHeaders;
            let length = headerData.subHeaders.length;
            for (let i = 0; i < length; i++) {
                let subHeader = this.#createHeader(subHeaders[i], index, HEADER);
                subheader_container.appendChild(subHeader.element);
            }
            headerElement.appendChild(subheader_container);
        }
        if (headerData.columns) {
            if (headerData.drawerColumns) {
                for (let i = 0; i < headerData.drawerColumns.length; i++) {
                    let colIndex = headerData.drawerColumns[i];
                    headerData.columns[colIndex].hidden = true;
                }
            }
            const columnBlock = this.#createAndPopulateColumnBlock(headerData.columns, HEADER);
            this.column_blocks.push(columnBlock);
            HEADER.columnBlock = columnBlock;
            headerElement.appendChild(columnBlock.element);
            if (headerData.drawerColumns) {
                let length = headerData.drawerColumns.length;
                const drawerColumns = [];
                for (let i = 0; i < length; i++) {
                    let colIndex = headerData.drawerColumns[i];
                    let col = columnBlock.columns[colIndex];
                    drawerColumns.push(col);
                }
                const drawerButton = this.#createDrawerButton(drawerColumns, HEADER);
                HEADER.drawerButton = drawerButton;
                this.drawer_buttons.push(HEADER.drawerButton);
            }
            if (headerData.configureable) {
                dropdownTrigger = this.#createDropdownTrigger(columnBlock);
                titleElement.appendChild(dropdownTrigger);
            }
            headerData.columns.every(col => col.hidden) && HEADER.setVisibility(false);
        }
        HEADER.setTitle(headerData.title);
        return HEADER;
    }
    #createAndPopulateColumnBlock(columnData, header) {
        const columnBlockElement = document.createElement('div');
        columnBlockElement.classList.add('column-block');
        const COLUMN_BLOCK = {
            element: columnBlockElement,
            parentHeader: header,
            index: -1,
            width: 0,
            isPinned: false,
            columns: [],
            noOfVisibleColumns: 0,
            isJurisdiction: header.isJurisdiction,
            setIndex: (index) => {
                COLUMN_BLOCK.index = index;
                columnBlockElement.setAttribute('data-index', `${index}`);
            },
            setVisibility: (isVisible) => {
                columnBlockElement.classList.toggle('hidden', !isVisible);
                header.setVisibility(isVisible);
            }
        };
        columnBlockElement.rsObject = COLUMN_BLOCK;
        if (COLUMN_BLOCK.isJurisdiction) {
            columnBlockElement.setAttribute('data-jurisdiction', 'true');
        }
        COLUMN_BLOCK.setIndex(this.column_blocks.length);
        const dataLength = columnData.length;
        for (let i = 0; i < dataLength; i++) {
            const data = columnData[i];
            const col = this.#createColumn(COLUMN_BLOCK, data.title, data.hidden, data.width, data.isEditable, data.uuid, data.payload, data.description ?? '');
            col.setLocalIndex(i);
            COLUMN_BLOCK.columns.push(col);
            COLUMN_BLOCK.width += col.isVisible ? col.width : 0;
            if (col.isVisible) {
                columnBlockElement.appendChild(col.element);
                COLUMN_BLOCK.noOfVisibleColumns++;
            }
            this.columns.push(col);
        }
        return COLUMN_BLOCK;
    }
    #createColumn(parentColumnBlock, title, hidden, width, isEditable, uuid, payload, description) {
        const colWidth = width ?? RetentionSchedule.COLUMN_DEFAULT_WIDTH;
        const columnElement = document.createElement('div');
        columnElement.classList.add('column');
        columnElement.style.width = `${colWidth}px`;
        columnElement.dataset.title = title;
        columnElement.dataset.width = `${colWidth}`;
        const titleElement = document.createElement('div');
        titleElement.textContent = title;
        columnElement.appendChild(titleElement);
        let COLUMN = {
            element: columnElement,
            titleElement: titleElement,
            parentColumnBlock: parentColumnBlock,
            title: '',
            isEditable: isEditable,
            isPinned: false,
            temporaryPinnedElement: null,
            description: '',
            isVisible: hidden ? !hidden : true,
            index: -1,
            localIndex: -1,
            width: -1,
            uuid: uuid ?? '',
            payload: payload ?? {},
            initialWidth: colWidth,
            setPinned: async (isPinned) => {
                if (isPinned === COLUMN.isPinned)
                    return;
                COLUMN.isPinned = isPinned;
                if (isPinned) {
                    parentColumnBlock.isPinned = true;
                    const pinnedCol = this.#createPinnedColumn(COLUMN);
                    addPinIcon();
                    let o = this.pinned_column_container.getPinOffset(COLUMN.index);
                    let a = 0;
                    for (let i = 0; i < this.columns.length; i++) {
                        if (this.columns[i] === COLUMN) {
                            break;
                        }
                        if (this.columns[i].isVisible) {
                            a += this.columns[i].width;
                        }
                    }
                    pinnedCol.element.style.left = `${a}px`;
                    pinnedCol.setStartingPinOffset(o);
                    this.pinned_column_container.pinnedColumns.push(pinnedCol);
                    this.pinned_column_container.pinnedColumns.sort((a, b) => a.originalColumn.index - b.originalColumn.index);
                    let pinnedElementsToAnimate = this.pinned_column_container.fixStartingPinOffsets(COLUMN.index, COLUMN.width, '+');
                    this.pinned_column_container.renderContainer.innerHTML = '';
                    this.pinned_column_container.pinnedColumns.forEach(pinnedObj => this.pinned_column_container.renderContainer.appendChild(pinnedObj.element));
                    this.global_rows.forEach(row => row.createPinnedRowElements(pinnedCol));
                    COLUMN.temporaryPinnedElement = this.#createInvisibleColumn(COLUMN);
                    columnElement.replaceWith(COLUMN.temporaryPinnedElement);
                    columnElement.style.left = `${a}px`;
                    this.pinned_column_container.header.append(columnElement);
                    if (pinnedElementsToAnimate.length > 0) {
                        await gsap.to(pinnedElementsToAnimate, {
                            x: `+=${COLUMN.width}`,
                            duration: 0.2,
                        });
                    }
                    if (COLUMN.localIndex === parentColumnBlock.columns.length - 1) {
                        columnElement.classList.add('end');
                    }
                }
                else {
                    const pinnedCol = this.pinned_column_container.pinnedColumns.find(pinned => pinned.originalColumn === COLUMN);
                    if (!pinnedCol)
                        return;
                    removePinIcon();
                    this.pinned_column_container.pinnedColumns = this.pinned_column_container.pinnedColumns.filter(pinned => pinned.originalColumn !== COLUMN);
                    let pinnedElementsToAnimate = this.pinned_column_container.fixStartingPinOffsets(COLUMN.index, COLUMN.width, '-');
                    await gsap.timeline().to(pinnedElementsToAnimate, {
                        x: `-=${COLUMN.width}`,
                        duration: 0.3,
                    }).to([COLUMN.element, pinnedCol.element], {
                        x: 0,
                        duration: 0.3,
                        onStart: () => {
                            this.pinned_column_container.renderContainer.prepend(pinnedCol.element);
                            this.pinned_column_container.header.prepend(COLUMN.element);
                        },
                        onComplete: () => {
                            pinnedCol.element.remove();
                        }
                    }, 0);
                    if (!COLUMN.temporaryPinnedElement)
                        return;
                    COLUMN.temporaryPinnedElement.replaceWith(columnElement);
                    columnElement.classList.remove('end');
                    gsap.to([COLUMN.element], {
                        x: 0,
                        left: 0,
                        duration: 0,
                    });
                    this.global_rows.forEach(row => row.removePinnedRowElements(pinnedCol));
                    parentColumnBlock.isPinned = this.pinned_column_container.pinnedColumns.some(pinned => pinned.originalColumn.parentColumnBlock === parentColumnBlock);
                }
                this.scroll_container.dispatchEvent(new Event('scroll'));
                this.#renderCurrentViewport();
            },
            setDropdownItems: (items, { adjustCells = false } = {}) => {
                COLUMN.isEditable = items;
                COLUMN.dropdownItems = items.reduce((result, item) => {
                    if (Array.isArray(item.items)) {
                        item.items.forEach(({ id, text }) => {
                            result.set(String(id), text);
                        });
                    }
                    else {
                        Object.entries(item.items).forEach(([key, value]) => {
                            result.set(String(key), value);
                        });
                    }
                    return result;
                }, new Map());
                if (adjustCells) {
                    let len = this.global_rows.length;
                    for (let i = 0; i < len; i++) {
                        let row = this.global_rows[i];
                        let cell = row.cells[COLUMN.index];
                        if (!COLUMN.dropdownItems.has(cell.data))
                            cell.setDataAndTurnOffEditing('', false);
                    }
                }
            },
            setTitle: (title, { adjustCells = false, adjustBlock = false } = {}) => {
                COLUMN.title = title;
                COLUMN.titleElement.textContent = title;
                if (RetentionSchedule.GET_MINIMUM_TEXT_WIDTH(title) > COLUMN.width) {
                    COLUMN.setWidth(RetentionSchedule.GET_MINIMUM_TEXT_WIDTH(title), {
                        adjustCells,
                        adjustBlock,
                    });
                }
            },
            setDescription: (description) => {
                COLUMN.element.setAttribute('data-description', description);
                COLUMN.description = description;
            },
            setIndex: (index) => {
                COLUMN.index = index;
                columnElement.setAttribute('data-index', `${index}`);
            },
            setLocalIndex: (index) => {
                COLUMN.localIndex = index;
                columnElement.setAttribute('data-local-index', `${index}`);
            },
            setWidth: (width, { adjustCells = false, adjustBlock = false } = {}) => {
                if (adjustBlock) {
                    if (width > COLUMN.width) {
                        COLUMN.parentColumnBlock.width += width - COLUMN.width;
                    }
                    else {
                        COLUMN.parentColumnBlock.width -= COLUMN.width - width;
                    }
                }
                COLUMN.width = width;
                columnElement.style.width = `${width}px`;
                if (adjustCells) {
                    let len = this.global_rows.length;
                    for (let i = 0; i < len; i++) {
                        let row = this.global_rows[i];
                        let cell = row.cells[COLUMN.index];
                        cell.setWidth(width);
                        if (COLUMN.isPinned && cell.temporarilyPinnedElement) {
                            cell.temporarilyPinnedElement.style.width = `${width}px`;
                        }
                    }
                }
            },
            findIndexToInsertBefore: () => {
                let index = -1;
                for (let i = 0; i < parentColumnBlock.columns.length; i++) {
                    let col = parentColumnBlock.columns[i];
                    if (col.isVisible && col.index > COLUMN.index) {
                        index = col.index;
                        break;
                    }
                }
                return index;
            },
            setArbitaryVisibility: (isVisible) => {
                if (COLUMN.isVisible === isVisible)
                    return;
                COLUMN.isVisible = isVisible;
                parentColumnBlock.width += isVisible ? COLUMN.width : -COLUMN.width;
                parentColumnBlock.noOfVisibleColumns += isVisible ? 1 : -1;
                if (parentColumnBlock.noOfVisibleColumns === 0) {
                    parentColumnBlock.setVisibility(false);
                }
                else {
                    parentColumnBlock.setVisibility(true);
                }
            },
            moveLocallyTo: async (localIndex) => {
                if (COLUMN.localIndex === localIndex || localIndex < 0 || localIndex >= COLUMN.parentColumnBlock.columns.length)
                    return;
                const parentBlock = COLUMN.parentColumnBlock;
                const currentCol = COLUMN;
                const otherCol = parentBlock.columns[localIndex];
                const pinnedCols = this.pinned_column_container.pinnedColumns;
                this.pinned_column_container.pinnedColumns.forEach(pinned => {
                    pinned.originalColumn.setPinned(false);
                });
                const currentColAnimationElements = [currentCol.element, document.querySelectorAll(`.cell[data-index="${COLUMN.index}"]`)];
                const otherColAnimationElements = [otherCol.element, document.querySelectorAll(`.cell[data-index="${otherCol.index}"]`)];
                const getOrientation = () => localIndex > COLUMN.localIndex ? 1 : -1;
                await gsap.timeline().to(currentColAnimationElements, {
                    x: getOrientation() * otherCol.width,
                    duration: 0.3,
                    onComplete: () => {
                        gsap.set(currentColAnimationElements, {
                            x: 0,
                            duration: 0,
                        });
                    }
                }).to(otherColAnimationElements, {
                    x: -getOrientation() * currentCol.width,
                    duration: 0.3,
                    onComplete: () => {
                        gsap.set(otherColAnimationElements, {
                            x: 0,
                            duration: 0,
                        });
                    }
                }, 0);
                this.columns[currentCol.index] = otherCol;
                this.columns[otherCol.index] = currentCol;
                parentBlock.columns[localIndex] = currentCol;
                parentBlock.columns[COLUMN.localIndex] = otherCol;
                if (currentCol.index < otherCol.index) {
                    parentBlock.element.insertBefore(currentCol.element, otherCol.element.nextSibling);
                }
                else {
                    parentBlock.element.insertBefore(currentCol.element, otherCol.element);
                }
                this.global_rows.forEach(row => {
                    const currentCell = row.cells[currentCol.index];
                    const otherCell = row.cells[otherCol.index];
                    const cellBlock = row.cellBlocks[parentBlock.index];
                    row.cells[currentCol.index] = otherCell;
                    row.cells[otherCol.index] = currentCell;
                    const currentCellElement = currentCell.element;
                    const otherCellElement = otherCell.element;
                    if (currentCell.index < otherCell.index) {
                        cellBlock.element.insertBefore(currentCellElement, otherCellElement.nextSibling);
                    }
                    else {
                        cellBlock.element.insertBefore(currentCellElement, otherCellElement);
                    }
                    {
                        let t = currentCell.index;
                        currentCell.setIndex(otherCell.index);
                        otherCell.setIndex(t);
                    }
                });
                parentBlock.columns.forEach((col, i) => {
                    col.setLocalIndex(i);
                });
                this.columns.forEach((col, i) => {
                    col.setIndex(i);
                });
                pinnedCols.forEach(pinned => {
                    pinned.originalColumn.setPinned(true);
                });
                this.#renderCurrentViewport();
            }
        };
        columnElement.rsObject = COLUMN;
        COLUMN.setTitle(title);
        COLUMN.setIndex(this.columns.length);
        COLUMN.setWidth(colWidth);
        COLUMN.setDescription(description ?? '');
        if (Array.isArray(isEditable)) {
            COLUMN.setDropdownItems(isEditable);
        }
        {
            let isResizing = false, toastId = null, minWidth = 0;
            const resizeElement = document.createElement('div');
            resizeElement.classList.add('column-resize-dragger');
            const resizePadding = document.createElement('div');
            resizePadding.classList.add('column-resize-mousemove-padding');
            columnElement.appendChild(resizeElement);
            const resizeBorder = document.createElement('div');
            resizeBorder.classList.add('column-resize-border');
            resizeElement.appendChild(resizeBorder);
            const toggleSelectionBorder = (toggle) => {
                resizeBorder.classList.toggle('active', toggle);
                if (COLUMN.parentColumnBlock.columns[COLUMN.parentColumnBlock.columns.length - 1] === COLUMN) {
                    resizeElement.classList.toggle('last-column', toggle);
                }
            };
            let initialMouseX = 0;
            const endDrag = (e) => {
                isResizing = false;
                COLUMN.setWidth(COLUMN.width, { adjustCells: true, adjustBlock: true });
                if (toastId) {
                    window.toast.dismiss(toastId);
                    toastId = null;
                }
                this.events['columnResize']?.call(this, {
                    col: COLUMN,
                });
                resizePadding.remove();
                toggleSelectionBorder(false);
            };
            const startDrag = (e) => {
                if (COLUMN.isPinned)
                    return;
                initialMouseX = e.clientX;
                isResizing = true;
                toggleSelectionBorder(true);
                minWidth = RetentionSchedule.GET_MINIMUM_TEXT_WIDTH(COLUMN.title);
                document.body.appendChild(resizePadding);
            };
            resizeElement.onmouseenter = (e) => {
                if (COLUMN.isPinned) {
                    resizeElement.style.cursor = 'default';
                    return;
                }
                else {
                    resizeElement.style.cursor = 'ew-resize';
                }
                toggleSelectionBorder(true);
            };
            resizeElement.onmouseleave = (e) => {
                if (!isResizing) {
                    toggleSelectionBorder(false);
                }
            };
            resizeElement.onmousedown = startDrag;
            resizePadding.onmousemove = (e) => {
                if (isResizing) {
                    const diff = e.clientX - initialMouseX;
                    const newWidth = COLUMN.width + diff;
                    if (newWidth < minWidth) {
                        if (!toastId) {
                            toastId = window.toast.error('Cannot make the column smaller than the minimum width', {
                                duration: Infinity,
                            });
                        }
                        return;
                    }
                    else {
                        if (toastId) {
                            window.toast.dismiss(toastId);
                            toastId = null;
                        }
                    }
                    COLUMN.setWidth(newWidth, {
                        adjustBlock: true,
                    });
                    initialMouseX = e.clientX;
                    document.querySelectorAll(`.cell[data-index="${COLUMN.index}"]`).forEach((element) => {
                        element.style.width = `${newWidth}px`;
                    });
                    if (COLUMN.isPinned && COLUMN.temporaryPinnedElement) {
                        COLUMN.temporaryPinnedElement.style.width = `${newWidth}px`;
                        this.pinned_column_container.pinnedColumns.forEach((pinnedObj) => {
                            if (pinnedObj.originalColumn.index > COLUMN.index) {
                                pinnedObj.element.style.left = `${parseInt(pinnedObj.element.style.left) + diff}px`;
                                pinnedObj.originalColumn.element.style.left = `${parseInt(pinnedObj.originalColumn.element.style.left) + diff}px`;
                                pinnedObj.setStartingPinOffset(pinnedObj.startingPinOffset + diff);
                                pinnedObj.pinOffset += diff;
                            }
                        });
                    }
                }
            };
            resizePadding.onmouseup = endDrag;
        }
        return COLUMN;
        function removePinIcon() {
            COLUMN.element.querySelector('.pin-icon')?.remove();
        }
        function addPinIcon() {
            const icon = document.createElement('span');
            icon.classList.add('pin-icon');
            icon.innerHTML = `
						<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#46d2c4"><path d="m640-480 80 80v80H520v240l-40 40-40-40v-240H240v-80l80-80v-280h-40v-80h400v80h-40v280Zm-286 80h252l-46-46v-314H400v314l-46 46Zm126 0Z"/></svg>
					`;
            columnElement.prepend(icon);
            icon.onclick = () => {
                COLUMN.setPinned(false);
            };
            if (COLUMN.title === 'Retention') {
                icon.classList.add('retetion-icon');
            }
            (0, tippy_js_1.default)(icon, {
                content: 'Unpin this column',
                placement: 'top',
                theme: 'retention-schedule',
            });
        }
    }
    #createPinnedColumn(COLUMN) {
        const element = document.createElement('div');
        element.classList.add('pinned-column');
        element.style.width = `${COLUMN.width}px`;
        const pinned = {
            element: element,
            currentLeft: 0,
            isBeingPinned: false,
            originalColumn: COLUMN,
            alreadyPinnedOnce: false,
            pinOffset: 0,
            startingPinOffset: 0,
            setStartingPinOffset: (offset) => {
                pinned.startingPinOffset = offset;
            }
        };
        return pinned;
    }
    #createDropdownTrigger(columnBlock) {
        const container = document.createElement('div');
        container.classList.add('header-dropdown-trigger');
        const span = document.createElement('span');
        span.textContent = 'more_vert';
        const dropdown = document.createElement('div');
        dropdown.classList.add('retention-header-dropdown');
        span.dropdown = dropdown;
        columnBlock.dropdown = dropdown;
        const populateDropdown = (columnBlock) => {
            dropdown.innerHTML = '';
            const length = columnBlock.columns.length;
            const createDropdownItem = (col) => {
                const dropdownItem = document.createElement('div');
                dropdownItem.classList.add('retention-header-dropdown-item');
                dropdownItem.textContent = col.title;
                dropdownItem.setAttribute('data-col-index', `${col.index}`);
                const checkbox = document.createElement('input');
                checkbox.type = 'checkbox';
                checkbox.checked = col.isVisible;
                checkbox.onchange = (e) => {
                    e.stopPropagation();
                    if (!this.is_column_transitioning)
                        this.toggleColumn(col, checkbox.checked);
                    else {
                        checkbox.checked = !checkbox.checked;
                    }
                };
                dropdownItem.appendChild(checkbox);
                dropdownItem.onclick = (e) => {
                    e.stopPropagation();
                    if (e.target === checkbox)
                        return;
                    checkbox.click();
                };
                return dropdownItem;
            };
            for (let i = 0; i < length; i++) {
                const col = columnBlock.columns[i];
                const dropdownItem = createDropdownItem(col);
                dropdown.appendChild(dropdownItem);
            }
        };
        span.onclick = () => {
            if (this.currently_opened_header_dropdown) {
                this.currently_opened_header_dropdown.remove();
            }
            populateDropdown(columnBlock);
            container.appendChild(dropdown);
            this.currently_opened_header_dropdown = dropdown;
        };
        container.appendChild(span);
        return container;
    }
    #createDrawerButton(columns, header) {
        let button = document.createElement('button');
        button.classList.add('drawer-button');
        button.style.width = `${RetentionSchedule.DRAWER_BUTTON_WIDTH}px`;
        button.style.height = `${RetentionSchedule.DRAWER_BUTTON_WIDTH}px`;
        button.style.right = `${-RetentionSchedule.DRAWER_BUTTON_WIDTH / 2}px`;
        let span = document.createElement('span');
        span.textContent = 'arrow_forward_ios';
        button.appendChild(span);
        let DRAWER_BUTTON = {
            element: button,
            isDrawerOpen: false,
            header: header,
            toggleDrawer: () => {
                DRAWER_BUTTON.isDrawerOpen = !DRAWER_BUTTON.isDrawerOpen;
                DRAWER_BUTTON.element.classList.toggle('open', DRAWER_BUTTON.isDrawerOpen);
                if (DRAWER_BUTTON.isDrawerOpen) {
                    this.showColumns(columns, false);
                }
                else {
                    this.hideColumns(columns, false);
                }
            },
            show: (rowHeight) => {
                button.style.top = `${rowHeight + header.element.offsetHeight - RetentionSchedule.DRAWER_BUTTON_WIDTH / 2}px`;
                header.element.appendChild(button);
                if (button.getBoundingClientRect().top > window.innerHeight) {
                    button.style.top = `${(window.innerHeight - header.element.getBoundingClientRect().bottom) / 2 + header.element.offsetHeight}px`;
                }
            }
        };
        button.onclick = DRAWER_BUTTON.toggleDrawer;
        button.onmouseenter = () => {
            this.cell_edit_button.classList.add('forcefully-transparent');
            this.currently_hovered_row?.controlContainerElement.classList.add('forcefully-transparent');
        };
        button.onmouseleave = () => {
            this.cell_edit_button.classList.remove('forcefully-transparent');
            this.currently_hovered_row?.controlContainerElement.classList.remove('forcefully-transparent');
        };
        return DRAWER_BUTTON;
    }
    #appendTableToDOM(tableSelector) {
        let table = document.querySelector(tableSelector);
        if (table) {
            table.appendChild(this.wrapper);
        }
        else {
            console.error('tableSelector is not valid');
        }
    }
    #renderCurrentViewport() {
        if (this.DEBUG_MODE)
            console.time('rendering');
        this.#setIndices();
        this.#renderViewport();
        if (this.DEBUG_MODE)
            console.timeEnd('rendering');
    }
    #setIndices() {
        this.#setRowIndices();
        this.#setColumnIndices();
    }
    #setRowIndices() {
        let bodyRect = this.body.getBoundingClientRect();
        let scrollContainerRect = this.scroll_container.getBoundingClientRect();
        const numberOfRows = this.rows.length;
        {
            for (let i = 0; i < numberOfRows; i++) {
                if (this.rows[i].isVisible) {
                    this.starting_row = this.rows[i];
                    break;
                }
            }
            let acum = 0, diff = scrollContainerRect.top - bodyRect.top;
            let visible_rows = Math.ceil(this.scroll_container.offsetHeight / RetentionSchedule.INTRINSIC_ROW_HEIGHT) + 1;
            visible_rows > numberOfRows ? visible_rows = numberOfRows : visible_rows;
            for (let i = this.starting_row.localIndex; i < numberOfRows - visible_rows + 1 && acum < diff; i++) {
                const row = this.rows[i];
                row.render('down');
                acum += row.height;
                this.starting_row = row;
            }
            if (this.starting_row) {
                this.ending_row = this.rows[this.starting_row.localIndex + visible_rows - 1];
            }
        }
    }
    #setColumnIndices() {
        this.#setStartingColumnBlock();
        this.#setEndingColumnBlock();
    }
    #setStartingColumnBlock() {
        const bodyRect = this.body.getBoundingClientRect();
        const scrollContainerRect = this.scroll_container.getBoundingClientRect();
        const diff = scrollContainerRect.left - bodyRect.left;
        let acum = 0, numberOfColumnBlocks = this.column_blocks.length;
        this.starting_column_block = this.column_blocks[0];
        for (let i = 0; i < numberOfColumnBlocks && acum < diff; i++) {
            const columnBlock = this.column_blocks[i];
            this.rows_h_translate = acum;
            acum += columnBlock.width;
            this.starting_column_block = columnBlock;
        }
    }
    #setEndingColumnBlock() {
        const bodyRect = this.body.getBoundingClientRect();
        const scrollContainerRect = this.scroll_container.getBoundingClientRect();
        const diff = bodyRect.right - scrollContainerRect.right;
        let acum = 0, numberOfColumnBlocks = this.column_blocks.length;
        this.ending_column_block = this.column_blocks.at(-1);
        for (let i = numberOfColumnBlocks - 1; i >= 0 && acum < diff; i--) {
            const columnBlock = this.column_blocks[i];
            acum += columnBlock.width;
            this.ending_column_block = columnBlock;
        }
    }
    #renderViewport() {
        if (this.starting_row === undefined || this.ending_row === undefined)
            return;
        let docFragment = document.createDocumentFragment();
        let pinnedDocFrags = [];
        for (let i = this.starting_row.localIndex; i <= this.ending_row.localIndex; i++) {
            let row = this.rows[i];
            docFragment.appendChild(row.element);
            if (row.pinnedElements.length) {
                for (let j = 0; j < row.pinnedElements.length; j++) {
                    let e = row.pinnedElements[j];
                    let pinnedDocFrag = pinnedDocFrags[j];
                    if (pinnedDocFrag === undefined || pinnedDocFrag.pinnedCol !== e.pinnedColumn) {
                        pinnedDocFrag = {
                            frag: document.createDocumentFragment(),
                            pinnedCol: e.pinnedColumn
                        };
                        pinnedDocFrags.push(pinnedDocFrag);
                    }
                    pinnedDocFrag.frag.appendChild(e.element);
                }
            }
            row.render('down');
            row.syncCellBlocks();
        }
        this.render_container.replaceChildren(docFragment);
        pinnedDocFrags.forEach(({ pinnedCol, frag }) => {
            pinnedCol.element.replaceChildren(frag);
        });
    }
    showData() {
        this.wrapper.classList.remove('no-data');
        this.no_data_wrapper.remove();
        this.scroll_container.style.overflowY = 'auto';
        this.flattened_headers.forEach(header => header.drawerButton?.element.classList.remove('hidden'));
    }
    showNoData() {
        this.render_container.innerHTML = '';
        this.wrapper.classList.add('no-data');
        document.body.appendChild(this.no_data_wrapper);
        this.flattened_headers.forEach(header => header.drawerButton?.element.classList.add('hidden'));
        this.scroll_container.style.overflowY = 'hidden';
        this.no_data_wrapper.style.top = `${this.body.getBoundingClientRect().top + 100}px`;
    }
    #createCellBlock(parentColumnBlock, index, row) {
        const cellBlockElement = document.createElement('div');
        cellBlockElement.classList.add('cell-block');
        cellBlockElement.setAttribute('data-index', `${index}`);
        cellBlockElement.setAttribute('data-header-title', parentColumnBlock.parentHeader.title ?? '');
        if (parentColumnBlock.isJurisdiction) {
            cellBlockElement.setAttribute('data-jurisdiction', 'true');
        }
        const CELL_BLOCK = {
            parentColumnBlock: parentColumnBlock,
            element: cellBlockElement,
            isJurisdiction: parentColumnBlock.isJurisdiction,
            index: index,
            cells: [],
            row: row,
            appendToRow: (row) => {
                row.element.appendChild(CELL_BLOCK.element);
            },
            prependToRow: (row) => {
                row.element.prepend(CELL_BLOCK.element);
            },
            remove: () => {
                CELL_BLOCK.element.remove();
            },
            setExternalEditLink: (link) => {
                if (!link) {
                    CELL_BLOCK.externalEditLink = undefined;
                    return;
                }
                CELL_BLOCK.externalEditLink = link;
            },
            setListeners: (row) => {
                if (!this.can_edit_cells)
                    return;
                CELL_BLOCK.element.onmouseenter = (e) => {
                    if (CELL_BLOCK.externalEditLink) {
                        this.cell_edit_button.show({
                            row: row,
                            cell: CELL_BLOCK.cells[0],
                            column: parentColumnBlock.columns[0],
                            elementToPrependTo: CELL_BLOCK.element
                        });
                    }
                    else {
                        for (let i = 0; i < CELL_BLOCK.cells.length; i++) {
                            const cell = CELL_BLOCK.cells[i];
                            const col = this.columns[cell.index];
                            if (!col.isEditable)
                                continue;
                            cell.element.onmouseenter = (e) => {
                                if (cell.isEditing)
                                    return;
                                this.cell_edit_button.show({
                                    row: row,
                                    cell: cell,
                                    column: col,
                                    elementToPrependTo: cell.element
                                });
                            };
                            cell.element.onmouseleave = (e) => {
                                if (e.relatedTarget === this.cell_edit_button)
                                    return;
                                this.cell_edit_button.hide();
                            };
                        }
                    }
                };
            },
            removeListeners: () => {
                if (!this.can_edit_cells)
                    return;
                CELL_BLOCK.element.onmouseenter = null;
                for (let i = 0; i < CELL_BLOCK.cells.length; i++) {
                    const cell = CELL_BLOCK.cells[i];
                    cell.element.onmouseenter = (e) => null;
                    cell.element.onmouseleave = (e) => null;
                }
            },
        };
        cellBlockElement.rsObject = CELL_BLOCK;
        return CELL_BLOCK;
    }
    #createSymbolElement({ value, innerHTML, tooltipInnerHTML }) {
        const div = document.createElement('div');
        div.classList.add('symbol');
        div.innerHTML = innerHTML;
        div.setAttribute('data-value', value);
        if (tooltipInnerHTML) {
            (0, tippy_js_1.default)(div, {
                content: tooltipInnerHTML,
                placement: 'top',
                allowHTML: true,
                theme: 'retention-schedule',
                animation: 'shift-away',
                arrow: true,
                hideOnClick: false,
                interactive: true,
                appendTo: () => document.body
            });
        }
        return div;
    }
    #createCell(parentCellBlock, col, isSymbol, cellData, index, width) {
        const isHTMLData = (cellData) => {
            return typeof cellData === 'object' && cellData.hasOwnProperty('data') && cellData.hasOwnProperty('html');
        };
        let cellElement = document.createElement('div');
        cellElement.classList.add('cell');
        cellElement.setAttribute('data-index', `${index}`);
        cellElement.setAttribute('data-column-title', col.title);
        cellElement.setAttribute('data-header-title', col.parentColumnBlock.parentHeader.title ?? '');
        let CELL = {
            element: cellElement,
            calculationElement: document.createElement('div'),
            temporarilyPinnedElement: null,
            parentCellBlock: parentCellBlock,
            row: parentCellBlock.row,
            column: col,
            data: '',
            index: index,
            isEditing: false,
            isSelected: false,
            type: 'plaintext',
            width: 0,
            isHighlighted: false,
            setWidth: (width) => {
                CELL.width = width;
                cellElement.style.width = `${width}px`;
                cellElement.dataset.width = `${width}`;
                CELL.calculationElement.style.width = `${width}px`;
            },
            setIndex: (index) => {
                CELL.index = index;
                cellElement.setAttribute('data-index', `${index}`);
            },
            disable: () => {
                CELL.element.classList.add('disabled');
            },
            enable: () => {
                CELL.element.classList.remove('disabled');
            },
            setIsSelected: (isSelected) => {
                if (CELL.isEditing)
                    return;
                if (isSelected) {
                    this.cells_selected.forEach(cell => cell.setIsSelected(false));
                    this.cells_selected = [];
                    this.cells_selected.push(CELL);
                    const targetElement = !CELL.parentCellBlock.isJurisdiction ? CELL.element : CELL.parentCellBlock.element;
                    targetElement.classList.add('outlined', `${!col.isEditable && !CELL.parentCellBlock.isJurisdiction ? 'not-editable' : null}`);
                }
                else {
                    const targetElement = !CELL.parentCellBlock.isJurisdiction ? CELL.element : CELL.parentCellBlock.element;
                    targetElement.classList.remove('outlined', 'not-editable');
                }
            },
            setIsEditing: (isEditing) => {
                CELL.setIsSelected(false);
                const cellElement = CELL.element, row = CELL.row, column = col;
                let resizeObserver;
                const resizeAndAdjustRowHeights = () => {
                    let processedRowHeight = row.processAndGetHeight();
                    let rowHeightDiff = processedRowHeight - row.height;
                    row.setHeight(processedRowHeight);
                    this.#adjustAllRowsVTranslate({
                        amount: rowHeightDiff,
                        startingIndex: row.localIndex + 1,
                    });
                };
                const search = (inputDiv, dropdown, dropdownItemClickHandler) => {
                    let inputValue = inputDiv.textContent?.toLowerCase().trim() ?? '';
                    let data = column.isEditable;
                    data = data.map((d) => {
                        let items;
                        if (Array.isArray(d.items)) {
                            items = d.items.filter(({ text }) => {
                                return text.toLowerCase().includes(inputValue);
                            });
                        }
                        else {
                            items = Object.fromEntries(Object.entries(d.items).filter(([key, value]) => {
                                return value.toLowerCase().includes(inputValue);
                            }));
                        }
                        if (Object.keys(items).length !== 0)
                            return {
                                groupName: d.groupName,
                                items: items,
                            };
                    }).filter((d) => d !== undefined);
                    dropdown.setData(data, dropdownItemClickHandler);
                    dropdown.setPosition();
                };
                if (isEditing && !CELL.isEditing) {
                    if (CELL.parentCellBlock.externalEditLink) {
                        window.__.createOneOffModal(CELL.parentCellBlock.externalEditLink);
                        return;
                    }
                    else if (!col.isEditable) {
                        window.toast('This cell is not editable', { type: 'error' });
                        return;
                    }
                    CELL.isEditing = isEditing;
                    CELL.element.classList.add('is-editing');
                    cellElement.onmouseenter = null;
                    cellElement.onmouseleave = null;
                    cellElement.textContent = '';
                    const saveButton = document.createElement('button');
                    saveButton.classList.add('cell-edit-save-button');
                    saveButton.textContent = 'check';
                    const cancelButton = document.createElement('button');
                    cancelButton.classList.add('cell-edit-cancel-button');
                    cancelButton.textContent = 'close';
                    const buttonContainer = document.createElement('div');
                    buttonContainer.classList.add('cell-edit-button-container');
                    buttonContainer.appendChild(saveButton);
                    buttonContainer.appendChild(cancelButton);
                    const inputWrapper = document.createElement('div');
                    inputWrapper.classList.add('cell-edit-input-wrapper');
                    cellElement.appendChild(inputWrapper);
                    cellElement.appendChild(buttonContainer);
                    const inputDiv = document.createElement('div');
                    inputDiv.classList.add('cell-edit-input');
                    inputDiv.setAttribute('data-value', CELL.data);
                    inputDiv.contentEditable = 'true';
                    inputDiv.textContent = CELL.data;
                    inputWrapper.appendChild(inputDiv);
                    inputDiv.focus();
                    const range = document.createRange();
                    const sel = window.getSelection();
                    range.selectNodeContents(inputDiv);
                    range.collapse(false);
                    sel?.removeAllRanges();
                    sel?.addRange(range);
                    resizeObserver = new ResizeObserver((entries) => {
                        if (inputWrapper.scrollHeight > inputWrapper.offsetHeight) {
                            let diff = inputWrapper.scrollHeight - inputWrapper.offsetHeight;
                            row.setHeight(row.height + diff);
                            this.#adjustAllRowsVTranslate({
                                amount: diff,
                                startingIndex: row.localIndex + 1,
                            });
                        }
                    });
                    resizeObserver.observe(inputDiv);
                    saveButton.onclick = (e) => {
                        CELL.setData(inputDiv.dataset.value ?? '');
                        resizeAndAdjustRowHeights();
                        this.#renderCurrentViewport();
                        CELL.setIsEditing(false);
                    };
                    cancelButton.onclick = (e) => {
                        CELL.setData(cellData, false);
                        CELL.setIsEditing(false);
                    };
                    if (column.isEditable === 'plaintext') {
                        cellElement.classList.add('plaintext');
                        inputDiv.oninput = (e) => {
                            inputDiv.setAttribute('data-value', inputDiv.textContent ?? '');
                        };
                    }
                    if (column.dropdownItems) {
                        cellElement.classList.add('dropdown');
                        inputDiv.textContent = '';
                        inputDiv.setAttribute('data-placeholder', column.dropdownItems.get(CELL.data) ?? 'Please select an item');
                        let dropdown = this.cell_edit_dropdown;
                        const dropdownItemClickHandler = (e, d) => {
                            inputDiv.setAttribute('data-placeholder', d.textContent ?? '');
                            inputDiv.setAttribute('data-value', d.dataset.value ?? '');
                            inputDiv.textContent = '';
                            dropdown.close();
                        };
                        dropdown.setData(column.isEditable, dropdownItemClickHandler);
                        dropdown.setControlElements(inputDiv, CELL);
                        dropdown.onClose = () => {
                            inputDiv.textContent = '';
                        };
                        dropdown.open();
                        inputDiv.oninput = (e) => {
                            search(inputDiv, dropdown, dropdownItemClickHandler);
                        };
                        inputDiv.onclick = (e) => {
                            if (dropdown.isOpen)
                                return;
                            dropdown.open();
                            search(inputDiv, dropdown, dropdownItemClickHandler);
                        };
                    }
                }
                else if (!isEditing && CELL.isEditing) {
                    CELL.isEditing = isEditing;
                    CELL.element.classList.remove('is-editing');
                    CELL.element.classList.remove('plaintext');
                    CELL.element.classList.remove('dropdown');
                    resizeAndAdjustRowHeights();
                    if (resizeObserver)
                        resizeObserver.disconnect();
                    this.#renderCurrentViewport();
                }
            },
            setData: (data, fireEvent = true) => {
                if (data !== undefined && data !== null) {
                    let cellData = isHTMLData(data) ? data.data : data.toString().trim();
                    isSymbol && (cellData = '');
                    const previousData = CELL.data;
                    CELL.data = cellData.trim();
                    CELL.element.setAttribute('data-value', CELL.data);
                    CELL.type = isHTMLData(data) ? 'html' : 'plaintext';
                    if (fireEvent) {
                        const row = this.rows[parseInt(CELL.parentCellBlock.element.parentElement?.dataset.index)];
                        this.events['cellDataSave']?.call(this, {
                            row: row,
                            header: CELL.parentCellBlock.parentColumnBlock.parentHeader,
                            column: this.columns[CELL.index],
                            cell: CELL,
                            previousData: previousData,
                            currentData: CELL.data,
                        });
                    }
                }
                const col = this.columns[CELL.index];
                if (col.dropdownItems) {
                    CELL.element.textContent = col.dropdownItems.get(CELL.data) ?? '';
                    CELL.calculationElement.textContent = col.dropdownItems.get(CELL.data) ?? '';
                }
                else {
                    if (CELL.data[0] === '<' && CELL.data[CELL.data.length - 1] === '>') {
                        CELL.element.innerHTML = CELL.data;
                    }
                    else if (CELL.type === 'html') {
                        CELL.element.innerHTML = cellData.html;
                    }
                    else {
                        CELL.element.textContent = CELL.data;
                    }
                    CELL.calculationElement.textContent = CELL.data;
                }
                CELL.calculationElement = cellElement.cloneNode(true);
            },
            setDataAndTurnOffEditing: (data, fireEvent = true) => {
                CELL.setData(data, fireEvent);
                CELL.setIsEditing(false);
            },
            _highlight: (term) => {
                const regex = new RegExp(term, 'gi');
                if (isHTMLData(cellData)) {
                    const div = cellElement.querySelector('.highlight-able');
                    div.innerHTML = div.innerHTML.replace(regex, (match) => `<span class="retention-highlight">${match}</span>`);
                }
                else {
                    cellElement.innerHTML = cellElement.innerHTML.replace(regex, (match) => `<span class="retention-highlight">${match}</span>`);
                }
            },
            highlight: (term) => {
                let data = Array.isArray(col.isEditable) ? col.dropdownItems?.get(CELL.data)?.toLowerCase() : CELL.data.toLowerCase();
                if (!data || !data.includes(term))
                    return;
                CELL._deHighlight();
                CELL._highlight(term);
                CELL.isHighlighted = true;
                this.highlighted_cells.add(CELL);
            },
            _deHighlight: () => {
                if (!isHTMLData(cellData)) {
                    cellElement.innerHTML = cellElement.innerHTML.replace(/<span class="retention-highlight">|<\/span>/g, '');
                }
                else {
                    const div = cellElement.querySelector('.highlight-able');
                    div.innerHTML = div.innerHTML.replace(/<span class="retention-highlight">|<\/span>/g, '');
                }
            },
            deHighlight: () => {
                CELL._deHighlight();
                CELL.isHighlighted = false;
                this.highlighted_cells.delete(CELL);
            }
        };
        CELL.setWidth(width);
        CELL.setData(cellData, false);
        cellElement.onclick = (e) => {
            if (e.detail === 1) {
                CELL.setIsSelected(true);
            }
            if (e.detail === 2 && this.can_edit_cells) {
                CELL.setIsEditing(true);
                e.stopPropagation();
            }
        };
        cellElement.rsObject = CELL;
        return CELL;
    }
    #createRow(index, rowId) {
        let rowElement = document.createElement('div');
        rowElement.classList.add('row');
        const numberRowElement = document.createElement('div');
        numberRowElement.classList.add('row-number');
        let calculationElement = rowElement.cloneNode(false);
        let controlContainer = document.createElement('div');
        controlContainer.classList.add('retention-schedule-row-controls-container');
        let moveRowChip = document.createElement('div');
        moveRowChip.classList.add('move-row-chip');
        moveRowChip.textContent = 'open_with';
        moveRowChip.draggable = true;
        moveRowChip.setAttribute('data-modal-trigger', '#move-row-modal');
        moveRowChip.setAttribute('data-modal-id', 'moveRowModal');
        moveRowChip.setAttribute('data-modal-clone', `true`);
        moveRowChip.setAttribute('data-row-id', `${rowId}`);
        let deleteRowChip = document.createElement('div');
        deleteRowChip.classList.add('delete-row-chip');
        deleteRowChip.textContent = 'delete';
        let addRowChip = document.createElement('div');
        addRowChip.classList.add('add-row-chip');
        let span = document.createElement('span');
        span.textContent = 'add';
        let p = document.createElement('p');
        p.textContent = 'Add row';
        addRowChip.appendChild(span);
        this.can_move_rows && controlContainer.appendChild(moveRowChip);
        this.can_add_rows && controlContainer.appendChild(addRowChip);
        this.can_delete_rows && controlContainer.appendChild(deleteRowChip);
        const ROW = {
            cells: [],
            cellBlocks: [],
            pinnedElements: [],
            localIndex: -1,
            globalIndex: -1,
            rowId: '',
            isRendered: false,
            element: rowElement,
            calculationElement: calculationElement,
            controlContainerElement: controlContainer,
            numberElement: numberRowElement,
            vTranslate: 0,
            hTranslate: 0,
            height: 0,
            isVisible: true,
            createPinnedRowElements: (pinnedCol) => {
                let COLUMN = pinnedCol.originalColumn;
                let cell = ROW.cells[COLUMN.index];
                cell.temporarilyPinnedElement = this.#createInvisibleCell(cell);
                cell.element.replaceWith(cell.temporarilyPinnedElement);
                let pinnedRow = this.#createPinnedRowElement(ROW);
                ROW.pinnedElements.push({
                    element: pinnedRow,
                    pinnedColumn: pinnedCol,
                });
                if (COLUMN.localIndex === COLUMN.parentColumnBlock.columns.length - 1) {
                    cell.element.classList.add('end');
                }
                pinnedRow.appendChild(cell.element);
            },
            removePinnedRowElements: (pinnedCol) => {
                let COLUMN = pinnedCol.originalColumn;
                let cell = ROW.cells[COLUMN.index];
                cell.temporarilyPinnedElement?.replaceWith(cell.element);
                cell.element.classList.remove('end');
                ROW.pinnedElements = ROW.pinnedElements.filter(pinned => pinned.pinnedColumn !== pinnedCol);
            },
            processAndGetHeight: () => {
                let renderRow = ROW.calculationElement;
                this.row_processor.appendChild(renderRow);
                let height = renderRow.offsetHeight;
                if (height < RetentionSchedule.INTRINSIC_ROW_HEIGHT)
                    height = RetentionSchedule.INTRINSIC_ROW_HEIGHT;
                renderRow.remove();
                return height;
            },
            setLocalIndex: (index) => {
                ROW.localIndex = index;
                ROW.element.setAttribute('data-index', `${index}`);
            },
            setGlobalIndex: (index) => {
                ROW.globalIndex = index;
                ROW.element.setAttribute('data-global-index', `${index}`);
            },
            setRowId: (rowId) => {
                ROW.rowId = rowId.toString();
                ROW.element.setAttribute('data-id', `${rowId}`);
                ROW.controlContainerElement.setAttribute('data-row-id', `${rowId}`);
                moveRowChip.setAttribute('data-row-id', `${rowId}`);
            },
            setIsRendered: (isRendered) => {
                if (isRendered === ROW.isRendered)
                    return;
                if (isRendered === false) {
                    ROW.isRendered = false;
                    if (ROW.localIndex !== -1) {
                        this.#setBodyHeightAndAdjustPinnedContainer(this.body_height - ROW.height + RetentionSchedule.INTRINSIC_ROW_HEIGHT);
                    }
                    ROW.setHeight(RetentionSchedule.INTRINSIC_ROW_HEIGHT);
                }
                else {
                    ROW.isRendered = isRendered;
                }
            },
            render: (direction) => {
                if (ROW.isRendered)
                    return;
                ROW.setIsRendered(true);
                ROW.setHeight(ROW.processAndGetHeight());
                this.#setBodyHeightAndAdjustPinnedContainer(this.body_height + ROW.height - RetentionSchedule.INTRINSIC_ROW_HEIGHT);
                let newRow = direction === 'down' ? this.rows[ROW.localIndex + 1] : direction === 'up' ? this.rows[ROW.localIndex - 1] : null;
                if (newRow) {
                    newRow.setTranslate({ vTranslate: ROW.vTranslate + ROW.height });
                }
            },
            setHeight: (height) => {
                if (height)
                    ROW.height = height;
                ROW.element.style.height = `${ROW.height}px`;
                ROW.pinnedElements.forEach((pinned) => {
                    pinned.element.style.height = `${ROW.height}px`;
                });
            },
            setTranslate: (props) => {
                if (props.vTranslate !== undefined)
                    ROW.vTranslate = props.vTranslate;
                if (props.hTranslate !== undefined)
                    ROW.hTranslate = props.hTranslate;
                ROW.element.style.transform = `translate3d(${ROW.hTranslate}px, ${ROW.vTranslate}px, 0px)`;
                ROW.pinnedElements.forEach((pinned) => {
                    pinned.element.style.transform = `translate3d(${0}px, ${ROW.vTranslate}px, 0px)`;
                });
            },
            syncCellBlocks: () => {
                if (this.starting_column_block === undefined || this.ending_column_block === undefined)
                    return;
                if (ROW.startingColumnBlock === this.starting_column_block && ROW.endingColumnBlock === this.ending_column_block)
                    return;
                if (this.DEBUG_MODE)
                    console.time('syncing cell block');
                const length = ROW.cellBlocks.length;
                for (let i = 0; i < length; i++) {
                    let cellBlock = ROW.cellBlocks[i];
                    cellBlock.remove();
                }
                for (let i = this.starting_column_block.index; i <= this.ending_column_block.index; i++) {
                    let cellBlock = ROW.cellBlocks[i];
                    cellBlock.appendToRow(ROW);
                }
                ROW.startingColumnBlock = this.starting_column_block;
                ROW.endingColumnBlock = this.ending_column_block;
                if (ROW.hTranslate !== this.rows_h_translate) {
                    ROW.setTranslate({ hTranslate: this.rows_h_translate });
                }
                if (this.DEBUG_MODE)
                    console.timeEnd('syncing cell block');
            },
            moveTo: (globalIndexToMoveTo, fireEvent = true) => {
                if (globalIndexToMoveTo >= this.global_rows.length)
                    globalIndexToMoveTo = this.global_rows.length - 1;
                if (globalIndexToMoveTo < 0)
                    globalIndexToMoveTo = 0;
                if (globalIndexToMoveTo === ROW.globalIndex)
                    return;
                let lastRow;
                if (ROW.localIndex !== -1) {
                    if (globalIndexToMoveTo > ROW.globalIndex) {
                        for (let i = ROW.globalIndex + 1; i <= globalIndexToMoveTo; i++) {
                            const row = this.global_rows[i];
                            row.setIsRendered(false);
                            row.setGlobalIndex(row.globalIndex - 1);
                            if (row.localIndex !== -1) {
                                lastRow = row;
                                row.setLocalIndex(row.localIndex - 1);
                                row.setTranslate({ vTranslate: row.vTranslate - ROW.height });
                            }
                        }
                        if (lastRow)
                            ROW.setTranslate({ vTranslate: lastRow.vTranslate + lastRow.height });
                    }
                    else {
                        for (let i = ROW.globalIndex - 1; i >= globalIndexToMoveTo; i--) {
                            const row = this.global_rows[i];
                            row.setIsRendered(false);
                            row.setGlobalIndex(row.globalIndex + 1);
                            if (row.localIndex !== -1) {
                                lastRow = row;
                                row.setLocalIndex(row.localIndex + 1);
                                row.setTranslate({ vTranslate: row.vTranslate + ROW.height });
                            }
                        }
                        if (lastRow)
                            ROW.setTranslate({ vTranslate: lastRow.vTranslate - ROW.height });
                    }
                }
                this.global_rows.splice(ROW.globalIndex, 1);
                this.global_rows.splice(globalIndexToMoveTo, 0, ROW);
                ROW.globalIndex = globalIndexToMoveTo;
                if (lastRow && ROW.localIndex !== -1) {
                    const localIndexToMoveTo = this.rows.indexOf(lastRow);
                    this.rows.splice(ROW.localIndex, 1);
                    this.rows.splice(localIndexToMoveTo, 0, ROW);
                    ROW.setLocalIndex(localIndexToMoveTo);
                }
                ROW.setIsRendered(false);
                this.#renderCurrentViewport();
                fireEvent && this.events['rowMoved']?.call(this, {
                    row: ROW,
                });
            },
            swapWith: (globalIndexToSwapWith, fireEvent = true) => {
                const firstRowIndex = ROW.globalIndex;
                ROW.moveTo(globalIndexToSwapWith, false);
                if (globalIndexToSwapWith > firstRowIndex) {
                    this.global_rows[globalIndexToSwapWith - 1].moveTo(firstRowIndex, false);
                }
                else {
                    this.global_rows[globalIndexToSwapWith + 1].moveTo(firstRowIndex, false);
                }
                fireEvent && this.events['rowMoved']?.call(this, {
                    row: ROW,
                });
            },
            addNewRow: (direction) => {
                let newRow = this.#createRow(ROW.localIndex, '');
                newRow.setTranslate({
                    vTranslate: ROW.vTranslate,
                    hTranslate: this.rows_h_translate,
                });
                const cellBlocks = [], cells = [];
                let cellIndex = 0, numberOfColumnBlocks = this.column_blocks.length;
                for (let j = 0; j < numberOfColumnBlocks; j++) {
                    const columnBlock = this.column_blocks[j];
                    let numOfColumns = columnBlock.columns.length;
                    const cellBlock = this.#createCellBlock(columnBlock, j, newRow);
                    cellBlocks.push(cellBlock);
                    for (let k = 0; k < numOfColumns; k++, cellIndex++) {
                        const col = columnBlock.columns[k];
                        const cell = this.#createCell(cellBlock, col, false, ``, cellIndex, col.width);
                        newRow.calculationElement.appendChild(cell.calculationElement);
                        if (col.isVisible)
                            cellBlock.element.appendChild(cell.element);
                        cellBlock.cells.push(cell);
                        cells.push(cell);
                    }
                }
                newRow.cellBlocks = cellBlocks;
                newRow.cells = cells;
                this.rows.splice(ROW.localIndex, 0, newRow);
                const index = this.global_rows.indexOf(ROW);
                this.global_rows.splice(index, 0, newRow);
                newRow.setGlobalIndex(index);
                newRow.render();
                this.#adjustAllRowsVTranslate({
                    amount: newRow.height,
                    startingIndex: newRow.localIndex + 1,
                    callback: (row) => {
                        row.setLocalIndex(row.localIndex + 1);
                    }
                });
                this.#syncRowsGlobalIndices();
                this.#renderCurrentViewport();
                ROW.element.dispatchEvent(new Event('mouseleave'));
                ROW.element.classList.remove('moving');
                this.events['rowAdded']?.call(this, {
                    previousRow: ROW,
                    newRow: newRow,
                });
                if (this.DEBUG_MODE)
                    console.timeEnd('add row');
            }
        };
        ROW.setLocalIndex(index);
        ROW.setRowId(rowId);
        ROW.setHeight(RetentionSchedule.INTRINSIC_ROW_HEIGHT);
        rowElement.rsObject = ROW;
        {
            addRowChip.onclick = (e) => {
                if (this.DEBUG_MODE)
                    console.time('add row');
                if (anime1)
                    anime1.pause();
                if (anime2)
                    anime2.pause();
                if (anime2)
                    anime2.reset();
                if (anime1)
                    anime1.reset();
                this.add_row_green_area.remove();
                ROW.addNewRow('above');
            };
            let anime1, anime2, row_animation_translate = 75, row_animation_duration = 200;
            addRowChip.onmouseenter = (e) => {
                if (this.is_column_transitioning)
                    return;
                if (this.is_row_dragging)
                    return;
                let greenAreaWrapper = this.add_row_green_area;
                greenAreaWrapper.style.top = `${ROW.vTranslate}px`;
                this.body.appendChild(greenAreaWrapper);
                anime1 = (0, anime_es_js_1.default)({
                    targets: ROW.element,
                    translateY: row_animation_translate,
                    duration: row_animation_duration,
                    delay: 300,
                    easing: 'easeInOutQuad',
                    update: (anim) => {
                        if (anim.progress > 50)
                            ROW.element.classList.add('moving');
                    }
                });
                anime2 = (0, anime_es_js_1.default)({
                    targets: greenAreaWrapper,
                    height: [0, row_animation_translate],
                    opacity: [0, 1],
                    duration: row_animation_duration,
                    delay: 300,
                    easing: 'easeInOutQuad',
                });
            };
            addRowChip.onmouseleave = (e) => {
                if (this.is_column_transitioning)
                    return;
                if (this.is_row_dragging)
                    return;
                if (anime1)
                    anime1.pause();
                if (anime2)
                    anime2.pause();
                anime1 = (0, anime_es_js_1.default)({
                    targets: ROW.element,
                    translateY: 0,
                    duration: row_animation_duration,
                    easing: 'easeInOutQuad',
                    complete: () => {
                        ROW.element.classList.remove('moving');
                    }
                });
                anime2 = (0, anime_es_js_1.default)({
                    targets: this.add_row_green_area,
                    height: 0,
                    opacity: 0,
                    duration: row_animation_duration,
                    easing: 'easeInOutQuad',
                    complete: () => {
                        this.add_row_green_area.remove();
                    }
                });
            };
            deleteRowChip.onclick = async (e) => {
                if (this.DEBUG_MODE)
                    console.time('delete row');
                const { isConfirmed } = await Swal_1.default.fire({
                    title: 'Are you sure you want to delete this row?',
                    text: 'This action cannot be undone',
                    icon: 'delete',
                    showCancelButton: true,
                    confirmButtonText: 'Delete',
                });
                if (!isConfirmed)
                    return;
                ROW.element.remove();
                ROW.pinnedElements.forEach((pinned) => pinned.element.remove());
                this.rows.splice(ROW.localIndex, 1);
                const index = this.global_rows.indexOf(ROW);
                this.global_rows.splice(index, 1);
                this.#adjustAllRowsVTranslate({
                    amount: -ROW.height,
                    startingIndex: ROW.localIndex,
                    callback: (row) => {
                        row.setLocalIndex(row.localIndex - 1);
                    }
                });
                this.#renderCurrentViewport();
                ROW.element.dispatchEvent(new Event('mouseleave'));
                ROW.element.classList.remove('moving');
                this.events['rowDeleted']?.call(this, {
                    deletedRow: ROW,
                });
                if (this.DEBUG_MODE)
                    console.timeEnd('delete row');
            };
            deleteRowChip.onmouseenter = (e) => {
                if (this.is_column_transitioning)
                    return;
                if (this.is_row_dragging)
                    return;
                ROW.element.classList.add('delete-row');
            };
            deleteRowChip.onmouseleave = (e) => {
                if (this.is_column_transitioning)
                    return;
                if (this.is_row_dragging)
                    return;
                ROW.element.classList.remove('delete-row');
            };
            let ghostRow, offsetX;
            moveRowChip.ondragstart = (e) => {
                if (this.rows.length !== this.global_rows.length) {
                    (0, tippy_js_1.default)(moveRowChip, {
                        content: 'Cannot drag rows on a searched schedule. Please clear the search first, or click the button to move lines explicitly',
                        placement: 'top',
                        theme: 'error-filled',
                        animation: 'shift-away',
                        arrow: true,
                        hideOnClick: false,
                        interactive: true,
                        showOnCreate: true,
                        appendTo: () => document.body,
                        onHidden: (e) => {
                            e.destroy();
                        }
                    });
                    return;
                }
                this.is_row_dragging = true;
                ghostRow = ROW.element.cloneNode(true);
                ghostRow.classList.add('ghost-row');
                ghostRow.style.transform = 'translate3d(0px, 0px, 0px)';
                ghostRow.style.zIndex = '100';
                ghostRow.style.opacity = '0';
                offsetX = ROW.element.getBoundingClientRect().left;
                ghostRow.style.left = `${offsetX}px`;
                document.body.appendChild(ghostRow);
                (0, anime_es_js_1.default)({
                    targets: ghostRow,
                    opacity: 0.8,
                    scale: 1.03,
                    duration: 200,
                    easing: 'easeInOutQuad',
                });
                const a = (0, anime_es_js_1.default)({
                    targets: ROW.element,
                    opacity: 0,
                    duration: 100,
                    easing: 'easeInOutQuad',
                    complete: () => {
                        ROW.element.classList.add('forcefully-transparent');
                        a.reset();
                    }
                });
            };
            moveRowChip.ondragend = (e) => {
                ghostRow.classList.add('releasing');
                (0, anime_es_js_1.default)({
                    targets: ghostRow,
                    opacity: 1,
                    scale: 1,
                    borderStyle: 'none',
                    top: ROW.element.getBoundingClientRect().top,
                    duration: 120,
                    easing: 'easeInOutQuad',
                    complete: () => {
                        ghostRow.remove();
                        ROW.element.classList.remove('forcefully-transparent');
                        this.is_row_dragging = false;
                        this.events['rowMoved']?.call(this, {
                            row: ROW,
                        });
                    }
                });
            };
            let movingNextRow, movingPreviousRow, movingRowDuration = 60;
            moveRowChip.ondrag = (e) => {
                if (e.screenX === 0 && e.screenY === 0)
                    return;
                {
                    const scrollAmount = 100;
                    const interval = 50;
                    const scrollContainerBottom = this.scroll_container.getBoundingClientRect().bottom;
                    const ghostRowBottom = ghostRow.getBoundingClientRect().bottom;
                    const ghostRowTop = ghostRow.getBoundingClientRect().top;
                    const ratio = 0.05;
                    if (ghostRowBottom > scrollContainerBottom * (1 - ratio) && ghostRowBottom < scrollContainerBottom) {
                        this.#scrollV(scrollAmount, interval);
                    }
                    else if (ghostRowTop < scrollContainerBottom * ratio && ghostRowTop > 0) {
                        this.#scrollV(-scrollAmount, interval);
                    }
                }
                {
                    ghostRow.style.top = `${e.clientY}px`;
                    const nextRow = this.global_rows[ROW.globalIndex + 1];
                    const previousRow = this.global_rows[ROW.globalIndex - 1];
                    if (nextRow && !movingNextRow && ghostRow.getBoundingClientRect().bottom > nextRow.element.getBoundingClientRect().top + nextRow.height * 0.75) {
                        movingNextRow = (0, anime_es_js_1.default)({
                            targets: nextRow.element,
                            translateY: -ROW.height,
                            duration: movingRowDuration,
                            easing: 'easeInOutQuad',
                            complete: () => {
                                movingNextRow.reset();
                                ROW.moveTo(nextRow.globalIndex, false);
                                movingNextRow = undefined;
                            }
                        });
                    }
                    if (previousRow && !movingPreviousRow && ghostRow.getBoundingClientRect().top < previousRow.element.getBoundingClientRect().top + previousRow.height * 0.25) {
                        movingPreviousRow = (0, anime_es_js_1.default)({
                            targets: previousRow.element,
                            translateY: ROW.height,
                            duration: movingRowDuration,
                            easing: 'easeInOutQuad',
                            complete: () => {
                                movingPreviousRow.reset();
                                ROW.moveTo(previousRow.globalIndex, false);
                                movingPreviousRow = undefined;
                            }
                        });
                    }
                }
            };
        }
        {
            controlContainer.resetTop = () => {
                controlContainer.style.top = `${ROW.element.getBoundingClientRect().top - controlContainer.offsetHeight / 2}px`;
            };
            const removeControlContainer = () => {
                controlContainer.style.opacity = '0';
                controlContainer.remove();
                this.currently_hovered_row = undefined;
            };
            const appendControlContainer = (e) => {
                if (controlContainer.childElementCount === 0 || this.is_row_resizing)
                    return;
                controlContainer.style.left = `${e.clientX - controlContainer.offsetWidth / 2}px`;
                this.currently_hovered_row = ROW;
            };
            rowElement.addEventListener('mousemove', (e) => {
                if (this.currently_hovered_row !== ROW)
                    return;
                const mouseX = e.clientX;
                const mouseY = e.clientY;
                const rowTop = ROW.element.getBoundingClientRect().top;
                const rowHeight = ROW.height;
                const containerRight = controlContainer.getBoundingClientRect().right;
                const containerLeft = controlContainer.getBoundingClientRect().left;
                let opacity = `${mouseY < rowTop + rowHeight / 5 ? 1 : 0}`;
                controlContainer.resetTop();
                if (mouseY < rowTop + rowHeight / 6) {
                    if (mouseX > containerRight)
                        controlContainer.style.left = `${mouseX - controlContainer.offsetWidth}px`;
                    else if (mouseX < containerLeft)
                        controlContainer.style.left = `${mouseX}px`;
                }
                else {
                    controlContainer.style.left = `${mouseX - controlContainer.offsetWidth / 2}px`;
                }
                controlContainer.style.opacity = `${opacity}`;
            });
            rowElement.addEventListener('mouseenter', (e) => {
                if (this.currently_hovered_row === ROW)
                    return;
                appendControlContainer(e);
                if (ROW === this.rows[0]) {
                    controlContainer.style.zIndex = '10';
                }
                else {
                    controlContainer.style.zIndex = '1';
                }
            });
            rowElement.addEventListener('mouseleave', (e) => {
                if (controlContainer.contains(e.relatedTarget) || e.relatedTarget === controlContainer || e.relatedTarget === this.cell_edit_button)
                    return;
                removeControlContainer();
            });
            controlContainer.addEventListener('mouseleave', (e) => {
                if (rowElement.contains(e.relatedTarget) || e.relatedTarget === rowElement || e.relatedTarget === this.cell_edit_button)
                    return;
                removeControlContainer();
            });
        }
        {
            let initialMouseY = 0, toastId = null;
            const startDrag = (e) => {
                initialMouseY = e.clientY;
                rowElement.classList.add('row-resizing');
                this.is_row_resizing = true;
                document.body.style.userSelect = 'none';
            };
            const endDrag = (e) => {
                this.is_row_resizing = false;
                rowElement.classList.remove('row-resizing');
                if (toastId) {
                    window.toast.dismiss(toastId);
                    toastId = null;
                }
                document.body.style.userSelect = 'auto';
            };
            rowElement.onmousedown = (e) => {
                if (rowElement.dataset.resizeable === 'true') {
                    startDrag(e);
                }
            };
            rowElement.onmouseup = (e) => {
                if (this.is_row_resizing) {
                    endDrag(e);
                }
            };
        }
        return ROW;
    }
    #syncRowsGlobalIndices(callback) {
        for (let i = 0; i < this.global_rows.length; i++) {
            this.global_rows[i].setGlobalIndex(i);
            if (callback)
                callback(this.global_rows[i]);
        }
    }
    #syncRowsLocalIndices(callback) {
        for (let i = 0; i < this.rows.length; i++) {
            this.rows[i].setLocalIndex(i);
            if (callback)
                callback(this.rows[i]);
        }
    }
    scroll_timeout;
    #scrollV(amount, interval) {
        if (this.scroll_timeout || amount === 0)
            return;
        this.scroll_timeout = setTimeout(() => {
            (0, anime_es_js_1.default)({
                targets: this.scroll_container,
                scrollTop: this.scroll_container.scrollTop + amount,
                duration: 80,
                easing: 'linear',
            });
            this.scroll_timeout = undefined;
        }, interval);
    }
    #adjustAllRowsVTranslate({ amount, startingIndex = 0, callback }) {
        const numberOfRows = this.rows.length;
        if (this.DEBUG_MODE)
            console.time('adjusting');
        for (let i = startingIndex; i < numberOfRows; i++) {
            let row = this.rows[i];
            row.setTranslate({ vTranslate: row.vTranslate + amount });
            if (callback)
                callback(row);
        }
        this.#setBodyHeightAndAdjustPinnedContainer(this.body_height + amount);
        if (this.DEBUG_MODE)
            console.timeEnd('adjusting');
    }
    #setBodyHeightAndAdjustPinnedContainer(height) {
        if (height)
            this.body_height = height;
        this.body.style.height = `${this.body_height}px`;
        const bottom = this.body.getBoundingClientRect().bottom;
        const bottomOfScreen = window.innerHeight;
        if (bottom < bottomOfScreen) {
            this.pinned_column_container.wrapper.style.height = `100%`;
        }
        else {
            this.pinned_column_container.wrapper.style.height = `calc(100vh - ${this.head.getBoundingClientRect().height + 24}px)`;
        }
        this.row_number_container.style.height = `${this.body_height}px`;
        gsap.to(this.row_number_container, {
            y: this.head.getBoundingClientRect().height + 2,
            duration: 0
        });
    }
    #displayRows(diff, isDown) {
        if (this.ending_row === undefined || this.starting_row === undefined)
            return;
        let accumulator = 0;
        const numberOfRows = this.rows.length;
        if (this.DEBUG_MODE)
            console.time('new row');
        while (accumulator < diff) {
            if (isDown) {
                if (this.ending_row.localIndex === numberOfRows - 1)
                    break;
                this.ending_row = this.rows[this.ending_row.localIndex + 1];
                this.ending_row.render('down');
                this.starting_row.element.replaceWith(this.ending_row.element);
                for (let i = 0; i < this.starting_row.pinnedElements.length; i++) {
                    this.starting_row.pinnedElements[i].element.replaceWith(this.ending_row.pinnedElements[i].element);
                }
                this.starting_row = this.rows[this.starting_row.localIndex + 1];
                accumulator += this.ending_row.height;
                this.ending_row.syncCellBlocks();
            }
            else {
                if (this.starting_row.localIndex === 0)
                    break;
                this.starting_row = this.rows[this.starting_row.localIndex - 1];
                this.starting_row.render('up');
                this.ending_row.element.replaceWith(this.starting_row.element);
                for (let i = 0; i < this.ending_row.pinnedElements.length; i++) {
                    this.ending_row.pinnedElements[i].element.replaceWith(this.starting_row.pinnedElements[i].element);
                }
                this.ending_row = this.rows[this.ending_row.localIndex - 1];
                accumulator += this.starting_row.height;
                this.starting_row.syncCellBlocks();
            }
        }
        if (this.DEBUG_MODE)
            console.timeEnd('new row');
    }
    #appendCellBlocks(diff, isRight) {
        if (this.ending_column_block === undefined || this.starting_column_block === undefined || this.starting_row === undefined || this.ending_row === undefined)
            return;
        let acum = 0;
        while (acum < diff) {
            if (isRight) {
                if (this.ending_column_block.index === this.column_blocks.length - 1)
                    break;
                this.ending_column_block = this.column_blocks[this.ending_column_block.index + 1];
                for (let i = this.starting_row.localIndex; i <= this.ending_row.localIndex; i++) {
                    let row = this.rows[i];
                    let cellBlock = row.cellBlocks[this.ending_column_block.index];
                    cellBlock.appendToRow(row);
                    row.endingColumnBlock = this.ending_column_block;
                }
                acum += this.ending_column_block.width;
            }
            else {
                if (this.starting_column_block.index === 0)
                    break;
                this.starting_column_block = this.column_blocks[this.starting_column_block.index - 1];
                this.rows_h_translate -= this.starting_column_block.width;
                for (let i = this.starting_row.localIndex; i <= this.ending_row.localIndex; i++) {
                    let row = this.rows[i];
                    let cellBlock = row.cellBlocks[this.starting_column_block.index];
                    cellBlock.prependToRow(row);
                    row.setTranslate({ hTranslate: this.rows_h_translate });
                    row.startingColumnBlock = this.starting_column_block;
                }
                acum += this.starting_column_block.width;
            }
        }
    }
    #removeCellBlocks(diff, isRight) {
        if (this.ending_column_block === undefined || this.starting_column_block === undefined || this.starting_row === undefined || this.ending_row === undefined)
            return;
        let acum = 0;
        while (true) {
            if (isRight) {
                acum += this.starting_column_block.width;
                if (acum > diff)
                    break;
                this.rows_h_translate += this.starting_column_block.width;
                this.starting_column_block = this.column_blocks[this.starting_column_block.index + 1];
                for (let i = this.starting_row.localIndex; i <= this.ending_row.localIndex; i++) {
                    let row = this.rows[i];
                    let cellBlock = row.cellBlocks[this.starting_column_block.index - 1];
                    cellBlock.remove();
                    row.setTranslate({ hTranslate: this.rows_h_translate });
                    row.startingColumnBlock = this.starting_column_block;
                }
            }
            else {
                acum += this.ending_column_block.width;
                if (acum > diff)
                    break;
                this.ending_column_block = this.column_blocks[this.ending_column_block.index - 1];
                for (let i = this.starting_row.localIndex; i <= this.ending_row.localIndex; i++) {
                    let row = this.rows[i];
                    let cellBlock = row.cellBlocks[this.ending_column_block.index + 1];
                    cellBlock.remove();
                    row.endingColumnBlock = this.ending_column_block;
                }
            }
        }
    }
}
exports.default = RetentionSchedule;
